/**
 * Comportements propres aux sections Ambinoid (`amb-*`).
 *
 * Chargé une seule fois, en `defer`, depuis snippets/scripts.liquid : au moment
 * où `init()` s'exécute le DOM est intégralement parsé.
 *
 * Ce fichier est vérifié par TypeScript via assets/jsconfig.json (`checkJs`) :
 * `npm run typecheck` doit rester vert. Les types viennent de assets/ambinoid.d.ts.
 */
(function () {
  'use strict';

  /**
   * La révision de l'API Klaviyo contre laquelle les corps de ce fichier sont
   * écrits.
   *
   * Klaviyo verse chaque changement de schéma dans une révision datée et sert
   * l'ancienne indéfiniment : l'épingler est ce qui empêche un changement chez
   * eux de casser ces appels-ci. Sans en-tête `revision`, l'appel est refusé.
   *
   * Deux fonctions s'en servent, et elles ne se connaissent pas : `subscribe()`
   * pour l'infolettre du pied de page, `initRestock()` pour l'alerte de retour
   * en stock. D'où sa place ici, au-dessus des deux.
   */
  const KLAVIYO_REVISION = '2026-07-15';

  /**
   * Formate un montant en centimes avec le `money_format` de la boutique.
   *
   * @param {number} cents - Montant en centimes.
   * @param {string} format - `money_format` de la boutique (ex. "{{amount}} €").
   * @returns {string}
   */
  function formatMoney(cents, format) {
    // Formateur autonome : la page ne charge pas option_selection.js, donc
    // Shopify.formatMoney n'est présent que si un autre script l'a injecté.
    const shopify = window.Shopify;
    if (shopify && typeof shopify.formatMoney === 'function' && format) {
      return shopify.formatMoney(cents, format);
    }
    // Virgule décimale et point des milliers, comme le `| money` de la
    // boutique : sans le point, « 10.000,00 € » rendu par le Liquid devenait
    // « 10000,00 € » au premier clic sur un format.
    const [whole = '0', fraction = '00'] = (cents / 100).toFixed(2).split('.');
    const amount = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + fraction;
    if (format && format.indexOf('{{') !== -1) {
      return format.replace(/\{\{\s*amount\S*\s*\}\}/, amount);
    }
    return amount + ' €';
  }

  /**
   * Ramène l'unité de contenu d'une variante au gramme ou au millilitre.
   * Miroir du `case` de snippets/ambinoid-unit-price.liquid : les deux
   * doivent couvrir les mêmes unités, sinon le prix changerait de forme au
   * premier clic.
   *
   * @type {Record<string, { factor: number, base: string, large: string }>}
   */
  const UNIT_SCALE = {
    mg: { factor: 0.001, base: 'g', large: 'kg' },
    g: { factor: 1, base: 'g', large: 'kg' },
    kg: { factor: 1000, base: 'g', large: 'kg' },
    ml: { factor: 1, base: 'ml', large: 'l' },
    cl: { factor: 10, base: 'ml', large: 'l' },
    l: { factor: 1000, base: 'ml', large: 'l' },
  };

  /**
   * Texte du prix à l'unité d'une variante — « · 10,00 €/g », suivi du prix
   * au kilo ou au litre à partir de 10 g ou 10 ml. Voir l'en-tête de
   * snippets/ambinoid-unit-price.liquid pour la règle et sa source.
   *
   * @param {AmbVariant} variant - La variante retenue.
   * @param {string} moneyFormat - `money_format` de la boutique.
   * @returns {string|null} Le texte, ou `null` si la variante n'a pas de prix à l'unité.
   */
  function unitPriceText(variant, moneyFormat) {
    if (variant.unit_price == null) return null;
    const measurement = variant.unit_price_measurement || {};
    const scale = UNIT_SCALE[String(measurement.quantity_unit || '').toLowerCase()];
    const quantity = scale ? Number(measurement.quantity_value) * scale.factor : 0;

    if (scale && quantity > 0) {
      let text = '· ' + formatMoney(Math.round(variant.price / quantity), moneyFormat) + '/' + scale.base;
      if (quantity >= 10) {
        text +=
          ' (' + formatMoney(Math.round((variant.price * 1000) / quantity), moneyFormat) + '/' + scale.large + ')';
      }
      return text;
    }

    // Unité hors du tableau (longueur, surface…) : le prix de référence que
    // Shopify calcule, tel quel.
    const reference =
      measurement.reference_value && measurement.reference_value !== 1 ? measurement.reference_value + ' ' : '';
    return '· ' + formatMoney(variant.unit_price, moneyFormat) + '/' + reference + (measurement.reference_unit || '');
  }

  /**
   * Lit le payload `data-amb-product-json` d'une section produit.
   *
   * Les sections émettent `{{ product.variants | json }}`, donc un tableau.
   * On accepte aussi la forme `{ variants: [...] }` pour rester tolérant si le
   * payload change côté Liquid.
   *
   * @param {Element} dataEl - Le <script type="application/json">.
   * @returns {AmbVariant[]} Les variantes, ou un tableau vide si illisible.
   */
  function parseVariants(dataEl) {
    try {
      const parsed = JSON.parse(dataEl.textContent || 'null');
      if (Array.isArray(parsed)) return parsed;
      if (parsed && Array.isArray(parsed.variants)) return parsed.variants;
      return [];
    } catch (error) {
      return [];
    }
  }

  /**
   * Les coloris que la galerie sait reconnaître, en minuscules.
   *
   * Lus dans l'alt des images, jamais dans le titre des variantes : les valeurs
   * d'option sont traduites sur la page allemande (Schwarz, Anthrazit), les alt
   * ne le sont pas. Un nouveau coloris se gagne en ajoutant son mot ici — et en
   * le mettant dans l'alt de chacune de ses images, variante liée comprise.
   */
  const GALLERY_COLOURS = ['black', 'anthracite'];

  /**
   * Le coloris d'une image, d'après son alt.
   *
   * Par mot entier : « blackberry » ne dit pas « black ».
   *
   * @param {string|null|undefined} alt - Le texte alternatif.
   * @returns {string} Le coloris, ou `''` pour une image commune.
   */
  function colourOf(alt) {
    const words = String(alt || '')
      .toLowerCase()
      .split(/[^a-z]+/);
    return GALLERY_COLOURS.find((colour) => words.includes(colour)) || '';
  }

  /**
   * Câble le formulaire produit : sélection de variante, prix, quantité, galerie.
   *
   * @param {HTMLElement} root - Élément portant `data-amb-product-root`.
   */
  function initProductForm(root) {
    if (root.dataset.ambReady === '1') return;
    const dataEl = root.querySelector('[data-amb-product-json]');
    if (!dataEl) return;
    root.dataset.ambReady = '1';

    const variants = parseVariants(dataEl);
    if (!variants.length) return;

    const idInputs = /** @type {HTMLInputElement[]} */ ([...root.querySelectorAll('input[name="id"]')]);
    const priceEls = /** @type {HTMLElement[]} */ ([...root.querySelectorAll('[data-amb-price]')]);
    const unitPriceEls = /** @type {HTMLElement[]} */ ([...root.querySelectorAll('[data-amb-unit-price]')]);
    const addBtns = /** @type {HTMLButtonElement[]} */ ([...root.querySelectorAll('[data-amb-add-btn]')]);
    const variantLabels = /** @type {HTMLElement[]} */ ([...root.querySelectorAll('[data-amb-buybar-variant]')]);
    const optionLabels = /** @type {HTMLElement[]} */ ([...root.querySelectorAll('[data-amb-option-label]')]);
    const sizeButtons = /** @type {HTMLElement[]} */ ([...root.querySelectorAll('[data-amb-size]')]);
    const moneyFormat = root.getAttribute('data-money-format') || '';
    const sizesWrap = /** @type {HTMLElement|null} */ (root.querySelector('[data-amb-sizes]'));
    const hint = /** @type {HTMLElement|null} */ (root.querySelector('[data-amb-variant-hint]'));

    // L'alerte de retour en stock, quand la section en rend une : elle suit le
    // format retenu, donc `selectVariant()` la déplie, la replie et l'efface.
    // `restockReset` n'est assigné qu'en fin de fonction — `selectVariant` ne
    // court qu'au clic, bien après.
    const restockBox = /** @type {HTMLElement|null} */ (root.querySelector('[data-amb-restock]'));
    /** @type {(() => void)|null} */
    let restockReset = null;

    // La galerie d'un produit à couleurs, quand il en est un : `selectVariant()`
    // lui passe la variante retenue. Assignée plus bas, avec les vignettes.
    /** @type {((variant: AmbVariant) => void)|null} */
    let galleryFor = null;

    // Le formulaire est lu depuis le champ `id` plutôt que par un sélecteur à
    // lui : c'est ce champ que la garde ci-dessous surveille, et `input.form`
    // désigne forcément le formulaire qui l'enverra.
    const idInput = idInputs[0] || null;
    const form = idInput ? idInput.form : null;

    // Vide tant que le client n'a rien choisi. Le Liquid part de cet état dès
    // que le produit a plus d'un format, sauf `?variant=` dans l'URL.
    let chosenId = idInput ? idInput.value : '';

    /**
     * @param {string} variantId - Id de la variante à sélectionner.
     * @param {boolean} updateSizeUI - true pour refléter la sélection sur les pastilles de taille.
     */
    function selectVariant(variantId, updateSizeUI) {
      const variant = variants.find((v) => String(v.id) === String(variantId));
      if (!variant) return;

      chosenId = String(variant.id);
      if (hint) hint.hidden = true;

      for (const input of idInputs) input.value = String(variant.id);
      for (const el of priceEls) el.textContent = formatMoney(variant.price, moneyFormat);
      for (const el of variantLabels) {
        el.textContent = variant.title;
        // La barre mobile portait l'invite à la place du format : elle a dit
        // ce qu'elle avait à dire.
        el.classList.remove('amb-buybar__label--prompt');
      }

      // L'en-tête de la rangée portait la même invite. Le choix fait, il
      // reprend le nom de l'option — « Weight », « Colour » : ce qui reste à
      // dire, c'est ce que la rangée range, pas ce qu'il faut y faire. Sa
      // couleur, elle, ne bouge pas : le rose tient à la classe.
      for (const el of optionLabels) {
        const optionName = el.getAttribute('data-label-option');
        if (optionName) el.textContent = optionName;
      }

      const unitText = unitPriceText(variant, moneyFormat);
      for (const el of unitPriceEls) {
        el.hidden = unitText == null;
        if (unitText != null) el.textContent = unitText;
      }

      for (const btn of addBtns) {
        btn.disabled = !variant.available;
        btn.textContent = variant.available
          ? btn.getAttribute('data-label-add')
          : btn.getAttribute('data-label-soldout');
      }

      // L'alerte s'ouvre sur un format épuisé et se referme sur les autres. La
      // remise à zéro est le point qui ne se devine pas : une confirmation
      // laissée en place après un changement de format annoncerait une alerte
      // sur un format que le client vient de quitter, et Klaviyo n'abonne qu'à
      // une variante à la fois.
      if (restockBox) {
        restockBox.dataset.ambRestockVariant = String(variant.id);
        // C'est le bouton qui se replie, pas l'enveloppe : elle porte le
        // `<dialog>`, et un dialogue dans un sous-arbre `hidden` hérite du
        // `display: none` — `showModal()` n'ouvrirait rien de visible.
        const opener = restockBox.querySelector('[data-amb-restock-open]');
        if (opener instanceof HTMLElement) opener.hidden = variant.available;

        // La rangée du dialogue porte la sélection, **d'où qu'elle vienne**. Une
        // fois la modale ouverte, les pastilles de la fiche ne sont plus
        // atteignables — c'est ce que « modale » veut dire — donc c'est elle qui
        // dit de quel format on parle, et elle seule.
        //
        // C'est ici que ça se joue plutôt que dans `initRestock` : un format
        // choisi sur la fiche passait par ce chemin-là et ne marquait rien,
        // pendant qu'un format choisi dans le dialogue marquait pour son compte.
        // Deux chemins, deux résultats. Il n'y en a plus qu'un — le clic du
        // dialogue appelle cette fonction comme une pastille le ferait.
        for (const btn of restockBox.querySelectorAll('[data-amb-restock-pick-variant]')) {
          const isThisOne = btn.getAttribute('data-amb-restock-pick-variant') === String(variant.id);
          btn.classList.toggle('is-selected', isThisOne);
          btn.setAttribute('aria-pressed', isThisOne ? 'true' : 'false');
        }

        if (restockReset) restockReset();
      }

      if (galleryFor) galleryFor(variant);

      if (updateSizeUI) {
        for (const btn of sizeButtons) {
          const isThisOne = btn.getAttribute('data-amb-size') === String(variantId);
          btn.classList.toggle('is-selected', isThisOne);
          btn.setAttribute('aria-pressed', isThisOne ? 'true' : 'false');
        }
      }
    }

    for (const btn of sizeButtons) {
      btn.addEventListener('click', function () {
        if (btn.hasAttribute('disabled')) return;
        const id = btn.getAttribute('data-amb-size');
        if (id) selectVariant(id, true);
      });
    }

    /**
     * Renvoie le client au choix du format : l'invite s'affiche, l'écran
     * descend sur la rangée des tailles, le focus part sur le premier format
     * encore en stock.
     *
     * `preventScroll` sur le focus : sans lui le navigateur ramènerait
     * lui-même le bouton à l'écran, d'un coup, et couperait le défilement
     * doux qu'on vient de lancer.
     */
    function askForVariant() {
      if (hint) hint.hidden = false;

      const target = sizesWrap || sizeButtons[0];
      if (target) {
        const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        target.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'center' });
      }

      // Le focus va sur un format qu'on peut acheter : c'est un « Add to cart »
      // resté sans choix qui nous amène ici, et c'est désormais le seul chemin —
      // l'alerte de retour en stock porte sa propre rangée dans son dialogue, et
      // n'a plus à renvoyer ici. Quand aucun format n'est achetable, le focus va
      // sur le premier de la rangée quand même : il n'y a rien de mieux à
      // désigner, et laisser le focus où il était ne dirait rien.
      const buyable = sizeButtons.find(
        (btn) => !btn.hasAttribute('disabled') && !btn.hasAttribute('data-amb-size-soldout')
      );
      const first = buyable || sizeButtons[0];
      if (first) first.focus({ preventScroll: true });
    }

    // Garde d'envoi. Le champ `id` vide part quand même chez Shopify si on le
    // laisse faire : /cart/add.js répond 422, le repli re-soumet en navigation
    // classique et le client atterrit sur une page d'erreur. Le tiroir, lui,
    // écoute `submit` sur `document` — d'où le `stopPropagation`, sans quoi il
    // lancerait la requête malgré le `preventDefault`.
    if (form && sizeButtons.length) {
      form.addEventListener('submit', function (event) {
        if (chosenId) return;
        event.preventDefault();
        event.stopPropagation();
        askForVariant();
      });
    }

    // Sélecteur de quantité
    const qtyWrap = root.querySelector('[data-amb-qty]');
    if (qtyWrap) {
      const qtyInput = /** @type {HTMLInputElement|null} */ (qtyWrap.querySelector('input'));
      if (qtyInput) {
        for (const btn of qtyWrap.querySelectorAll('button')) {
          btn.addEventListener('click', function () {
            const step = parseInt(btn.getAttribute('data-step') || '0', 10) || 0;
            const next = (parseInt(qtyInput.value, 10) || 1) + step;
            qtyInput.value = String(next < 1 ? 1 : next);
          });
        }
      }
    }

    // Miniatures de la galerie
    const mainImg = /** @type {HTMLImageElement|null} */ (root.querySelector('[data-amb-main-image]'));
    const thumbs = /** @type {HTMLElement[]} */ ([...root.querySelectorAll('[data-amb-thumb]')]);
    // Compteur posé sur la photo en mobile. Le total qu'il annonce est celui
    // des vignettes visibles, pas celui des images du produit : le Liquid les
    // plafonne au même nombre, et une image hors rangée serait inatteignable —
    // une vignette repliée par le choix d'un coloris aussi.
    const counter = /** @type {HTMLElement|null} */ (root.querySelector('[data-amb-gallery-count]'));

    /** @param {HTMLElement} thumb - La vignette à porter sur la photo principale. */
    function showThumb(thumb) {
      const full = thumb.getAttribute('data-full');
      if (!mainImg || !full) return;
      // Déjà sur la photo : rien à recharger. C'est le cas d'un `?variant=` dont
      // l'image ouvre la galerie — le srcset du Liquid reste alors en place.
      if (!thumb.classList.contains('is-active')) {
        mainImg.src = full;
        // La galerie sert des images responsive : le srcset primerait sur le
        // src qu'on vient de poser, il faut donc le neutraliser.
        mainImg.removeAttribute('srcset');
        const img = thumb.querySelector('img');
        if (img) mainImg.alt = img.alt;
        for (const other of thumbs) other.classList.remove('is-active');
        thumb.classList.add('is-active');
      }
      const visible = thumbs.filter((other) => !other.hidden);
      if (counter) counter.textContent = visible.indexOf(thumb) + 1 + ' / ' + visible.length;
    }

    for (const thumb of thumbs) {
      thumb.addEventListener('click', function () {
        showThumb(thumb);
      });
    }

    // Galerie par coloris. Elle ne s'arme que si au moins deux variantes ont
    // une image liée différente : une fleur, un hasch — une seule photo pour
    // tous les poids, ou aucune liée — gardent leur galerie telle quelle.
    //
    // Le coloris d'une variante est lu dans l'alt de son image liée, comme
    // celui des vignettes dans le leur : les deux parlent la même langue,
    // quelle que soit celle de la page. Une vignette dont l'alt ne nomme aucun
    // coloris est commune — la boîte, l'emballage — et reste visible partout.
    const linked = new Set(variants.map((v) => (v.featured_image ? v.featured_image.id : null)).filter(Boolean));
    if (thumbs.length > 1 && linked.size > 1) {
      galleryFor = function (variant) {
        const colour = colourOf(variant.featured_image && variant.featured_image.alt);
        for (const thumb of thumbs) {
          const own = colourOf(thumb.getAttribute('data-amb-alt'));
          thumb.hidden = colour !== '' && own !== '' && own !== colour;
        }
        const first = thumbs.find((thumb) => !thumb.hidden);
        if (!first) return;
        showThumb(first);
        // Sur téléphone la rangée défile de côté : elle repart du début, où
        // est la vignette qu'on vient de porter sur la photo.
        if (first.parentElement) first.parentElement.scrollLeft = 0;
      };

      // Tant que rien n'est choisi, toutes les images, dans l'ordre du Liquid.
      // Un `?variant=` vaut choix, et la galerie le suit dès l'arrivée.
      const preset = chosenId ? variants.find((v) => String(v.id) === chosenId) : null;
      if (preset) galleryFor(preset);
    }

    // En dernier : `selectVariant` lui est passé, et la rangée de formats du
    // dialogue s'en sert. Elle ne retient donc pas le format pour son compte —
    // c'est la fiche entière qui suit, pastilles et barre d'achat comprises.
    if (restockBox) restockReset = initRestock(restockBox, selectVariant);
  }

  /* ---------- alerte de retour en stock ---------- */

  /**
   * Câble le formulaire « préviens-moi » posé sous un format épuisé.
   *
   * Il pose un abonnement Klaviyo *par variante* — `$shopify:::$default:::<id>`,
   * la forme sous laquelle l'intégration Shopify range le catalogue. C'est cet
   * abonnement, et rien d'autre, qui produit la métrique « Subscribed to Back in
   * Stock » sur laquelle le flux « Back in Stock — Notify Me » est déclenché.
   * Les deux moitiés vivent dans deux systèmes qui ne se relisent pas : retirer
   * ce formulaire laisse un flux vert et vide, et aucun linter du dépôt ne le
   * verra.
   *
   * **Le consentement est l'abonnement lui-même.** Klaviyo n'exige pas de
   * consentement marketing pour cet e-mail-là : l'adresse sert à une seule
   * notification, celle que le client vient de demander. Rien n'est ajouté à
   * l'infolettre, et c'est délibéré — le consentement infolettre est le double
   * opt-in de Shopify, seule source de confirmation de la boutique. Ajouter une
   * case « ajoutez-moi à la liste » ici ouvrirait une seconde porte à ce
   * consentement-là, ce que la décision du 8 septembre 2026 a écarté.
   *
   * **La langue part avec l'adresse, et c'est ce qui décide de la langue de
   * l'e-mail.** Le modèle Klaviyo bifurque sur `person.locale` ; un profil créé
   * par le seul abonnement n'en porte aucune, et la boutique allemande enverrait
   * donc de l'anglais sans que rien ne le signale. L'endpoint d'abonnement
   * n'accepte pas `locale` — il ne prend que l'adresse, le téléphone et
   * l'identifiant externe — d'où le second appel, à `/client/profiles`, qui pose
   * la locale sans rien consentir. Il part **après** l'abonnement et son échec
   * ne coûte que la langue : l'alerte, elle, est déjà prise.
   *
   * **Deux portes, et ce n'est pas la même qui les garde.** `preventDefault`
   * retient la soumission : sans lui le formulaire part en navigation classique
   * et emporte la page, adresse en clair dans l'URL. Le `stopPropagation`, lui,
   * est une ceinture et non la bretelle — le mini-panier écoute bien `submit`
   * sur `document`, mais il ne retient que les formulaires dont l'`action` finit
   * par `/cart/add` (voir `isAddToCartForm()`), et celui-ci n'a pas d'`action`
   * du tout. C'est cette absence qui le met hors de portée du tiroir, et c'est
   * elle que le test mesure : poser une `action` ici suffirait à expédier une
   * adresse e-mail au panier.
   *
   * La validation, elle, n'est pas faite ici : `required` et `type="email"`
   * suffisent, le navigateur retient la soumission et dit pourquoi. Un `submit`
   * qui arrive ici porte donc déjà une adresse valide.
   *
   * **Le dialogue est natif, et ouvert par `showModal()`.** C'est ce qui donne
   * le piège à focus, la fermeture par Échap et la promotion en top layer, donc
   * un dialogue que ni la barre d'achat ni un ancêtre qui rogne ne peuvent
   * couper. Ce que le natif ne donne pas et qui est écrit ici : le focus posé
   * sur le champ à l'ouverture — la demande —, le retour du focus au bouton à
   * la fermeture, et le clic hors panneau qui ferme.
   *
   * **Et le format se choisit ici quand il n'est pas déjà choisi.** Un dialogue
   * modal rend le reste de la page inerte : la rangée des pastilles n'est donc
   * ni cliquable ni atteignable tant qu'il est ouvert. Envoyé sans format, ce
   * formulaire se fermait pour y renvoyer — le client avait saisi son adresse,
   * appuyé, et la fenêtre disparaissait sans que rien ne soit enregistré ni
   * dit. Le dialogue porte maintenant sa propre rangée, et ne se ferme plus.
   *
   * @param {HTMLElement} box - Le bloc `[data-amb-restock]`.
   * @param {(variantId: string, updateSizeUI: boolean) => void} chooseVariant - Retient un format sur toute la fiche, comme le ferait un clic sur une pastille.
   * @returns {(() => void)|null} De quoi effacer l'issue affichée, ou null si le bloc est incomplet.
   */
  function initRestock(box, chooseVariant) {
    const form = /** @type {HTMLFormElement|null} */ (box.querySelector('[data-amb-restock-form]'));
    const field = /** @type {HTMLInputElement|null} */ (box.querySelector('[data-amb-restock-email]'));
    // La rangée de formats compte parmi les noeuds sans lesquels le bloc ne
    // fonctionne pas : c'est elle qui porte le seul chemin vers un format quand
    // aucun n'est retenu. Sans elle, l'envoi n'aurait rien à proposer.
    const pick = /** @type {HTMLElement|null} */ (box.querySelector('[data-amb-restock-pick]'));
    if (!form || !field || !pick) return null;

    const done = /** @type {HTMLElement|null} */ (box.querySelector('[data-amb-restock-done]'));
    const failed = /** @type {HTMLElement|null} */ (box.querySelector('[data-amb-restock-error]'));
    const doneEmail = /** @type {HTMLElement|null} */ (box.querySelector('[data-amb-restock-done-email]'));
    const needFormat = /** @type {HTMLElement|null} */ (box.querySelector('[data-amb-restock-need]'));
    const pickButtons = /** @type {HTMLElement[]} */ ([...box.querySelectorAll('[data-amb-restock-pick-variant]')]);
    const company = box.dataset.ambRestockCompany || '';
    const locale = box.dataset.ambRestockLocale || '';
    const modal = /** @type {HTMLDialogElement|null} */ (box.querySelector('[data-amb-restock-modal]'));
    const opener = /** @type {HTMLElement|null} */ (box.querySelector('[data-amb-restock-open]'));

    /**
     * Déplie une issue et lui donne le focus.
     *
     * Différé d'une trame : `role="status"` ne fait rien d'un message déjà
     * présent, et le focus posé dans la même trame que le dépliage ne tient pas
     * toujours. `preventScroll` parce que le bloc est déjà sous les yeux.
     *
     * @param {HTMLElement|null} el - Le message à montrer.
     */
    function show(el) {
      if (!el) return;
      el.hidden = false;
      requestAnimationFrame(() => el.focus({ preventScroll: true }));
    }

    /**
     * Remet le bloc dans l'état où le Liquid l'a rendu.
     *
     * Écrite en `const` et non en déclaration : une déclaration est hissée au
     * haut de la fonction, donc utilisable avant la garde ci-dessus, et
     * TypeScript refuse alors d'y tenir `form` pour non nul.
     */
    const reset = () => {
      if (done) done.hidden = true;
      if (failed) failed.hidden = true;
      form.hidden = false;
      delete box.dataset.ambRestockState;
      box.removeAttribute('aria-busy');
    };

    /**
     * Pose la langue de la page sur le profil que l'abonnement vient de créer.
     *
     * Best effort, et volontairement silencieux à l'écran : l'alerte est déjà
     * enregistrée, et une phrase sur une langue que le client n'a pas demandée
     * ne lui apprendrait rien. L'échec part en console.
     *
     * @param {string} address - L'adresse que Klaviyo vient d'accepter.
     */
    function tagLocale(address) {
      if (!locale) return;
      fetch('https://a.klaviyo.com/client/profiles/?company_id=' + encodeURIComponent(company), {
        method: 'POST',
        headers: { 'content-type': 'application/json', revision: KLAVIYO_REVISION },
        body: JSON.stringify({
          data: { type: 'profile', attributes: { email: address, locale: locale } },
        }),
        keepalive: true,
      })
        .then((response) => {
          if (!response.ok) console.error('[ambinoid] restock locale', response.status);
        })
        .catch((error) => console.error('[ambinoid] restock locale', error));
    }

    /**
     * Ouvre le dialogue et pose le focus sur le champ.
     *
     * `showModal()` applique les étapes de focus de la spec et le champ porte
     * `autofocus` : **mesuré, cela suffit** — retirer l'appel ci-dessous ne fait
     * rougir aucun test, y compris celui de la seconde ouverture. Il reste
     * comme ceinture, contre les moteurs qui ont divergé sur le rejeu de
     * `autofocus` d'une ouverture à la suivante, et parce que le champ prêt à la
     * frappe est la demande, pas un agrément.
     *
     * Ce que les tests tiennent, c'est donc le *résultat* — le champ a le focus
     * après l'ouverture — et non cette ligne-ci. C'est le bon niveau : le jour
     * où la plateforme change d'avis, ils le disent.
     *
     * En `const` comme `reset()` plus bas, et pour la même raison : une
     * déclaration est hissée au haut de la fonction, donc utilisable avant la
     * garde qui écarte les noeuds manquants, et TypeScript refuse alors d'y
     * tenir `field` pour non nul.
     */
    const open = () => {
      if (!modal || typeof modal.showModal !== 'function' || modal.open) return;
      modal.showModal();

      // Compte connecté : le Liquid a prérempli le champ avec l'adresse du
      // compte, et il n'y a rien à saisir. Le drapeau est lu sur l'enveloppe et
      // non sur la valeur du champ — un visiteur non connecté qui a tapé son
      // adresse, refermé et rouvert en laisse une, et la lire suffirait à
      // l'inscrire sans qu'il ait rien demandé.
      if (box.dataset.ambRestockKnown !== undefined) {
        // Déjà inscrit : la confirmation est en place et on ne repart pas. Un
        // changement de format la retire (`reset()`), donc un client qui change
        // d'avis peut redemander — pour l'autre format, qui est une autre alerte.
        if (done && !done.hidden) return;
        // Le format est retenu : il ne manque plus rien, l'envoi part. Passer
        // par `requestSubmit()` plutôt que d'appeler le `fetch` ici, c'est
        // garder **un seul** chemin d'envoi — l'attente peinte, la locale, la
        // confirmation et l'échec sont écrits une fois.
        if (box.dataset.ambRestockVariant) {
          form.requestSubmit();
          return;
        }
        // Il manque le format, et lui seul : le focus part sur la rangée.
        if (pickButtons[0]) pickButtons[0].focus({ preventScroll: true });
        return;
      }

      field.focus();
    };

    if (opener && modal) {
      opener.addEventListener('click', open);

      // Échap et la croix passent tous deux par `close`, donc le retour du focus
      // est écrit une fois. Ceinture, là aussi : la spec veut que la fermeture
      // rende le focus à l'élément qui l'avait avant `showModal()`, donc au
      // bouton, et retirer ces trois lignes ne fait rougir aucun test. Elles
      // restent parce que le prix d'un manquement est un clavier qui repart du
      // haut du document, et le visiteur qui perd la carte d'achat qu'il lisait.
      modal.addEventListener('close', function () {
        opener.focus({ preventScroll: true });
      });

      const closer = box.querySelector('[data-amb-restock-close]');
      if (closer instanceof HTMLElement) closer.addEventListener('click', () => modal.close());

      // Le clic hors panneau. `<dialog>` ne le ferme pas de lui-même, et un clic
      // sur le `::backdrop` arrive avec le dialogue pour cible — mais un clic
      // sur le *padding* du dialogue aussi, et celui-là est à l'intérieur du
      // panneau. Comparer la cible ne suffit donc pas : fermer là-dessus
      // refermerait la modale sur un clic manqué à deux pixels du champ. On
      // compare les coordonnées à la boîte peinte.
      //
      // `detail > 0` écarte les clics sans pointeur — une touche Entrée sur un
      // bouton du dialogue produit un clic à (0, 0), donc hors de la boîte, donc
      // une fermeture que personne n'a demandée.
      modal.addEventListener('click', function (event) {
        if (event.target !== modal || event.detail === 0) return;
        const box = modal.getBoundingClientRect();
        const inside =
          event.clientX >= box.left &&
          event.clientX <= box.right &&
          event.clientY >= box.top &&
          event.clientY <= box.bottom;
        if (!inside) modal.close();
      });
    }

    // La rangée de formats du dialogue. Elle n'existe que sur un produit à
    // plusieurs variantes, et ne se voit que tant qu'aucune n'est retenue.
    //
    // Chaque bouton délègue à `selectVariant()`, qui met à jour la fiche
    // entière — prix, barre d'achat, pastilles, et le `data-amb-restock-variant`
    // que la soumission relit. Rien n'est recopié ici : un second chemin qui
    // poserait l'identifiant lui-même finirait par diverger du premier.
    for (const btn of pickButtons) {
      btn.addEventListener('click', function () {
        const id = btn.getAttribute('data-amb-restock-pick-variant');
        if (!id) return;
        // Y compris le marquage de la rangée : `selectVariant()` le fait pour
        // les trois provenances, il n'y a rien à refaire ici.
        chooseVariant(id, true);
        if (needFormat) needFormat.hidden = true;
      });
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      event.stopPropagation();

      // Le bloc peut être ouvert sans variante : un produit dont tous les
      // formats sont épuisés n'en coche aucun à l'arrivée, et c'est l'état de
      // presque tout le catalogue aujourd'hui. L'alerte est par format, et
      // deviner lequel serait deviner ce que le client attend.
      const variantId = box.dataset.ambRestockVariant;
      if (!variantId) {
        // On demande le format **ici**, et rien ne se ferme. C'est le correctif
        // du cul-de-sac : le dialogue se fermait pour renvoyer aux pastilles, et
        // le client voyait sa fenêtre disparaître après avoir saisi son adresse,
        // sans que rien ne soit enregistré ni dit.
        //
        // La rangée est toujours là quand on arrive ici, et les deux conditions
        // sont exactement complémentaires : le partiel la rend dès que le
        // produit a plus d'une variante, et la section retient d'office la
        // variante unique quand il n'en a qu'une (`chosen_variant`). Un produit
        // sans format retenu a donc forcément plusieurs formats, donc une
        // rangée. Il n'y a pas de repli à écrire — en écrire un remettrait la
        // fermeture, et personne ne passerait jamais dedans pour s'en
        // apercevoir.
        pick.hidden = false;
        if (needFormat) needFormat.hidden = false;
        if (pickButtons[0]) pickButtons[0].focus({ preventScroll: true });
        return;
      }
      if (!company || typeof window.fetch !== 'function') return;

      const address = field.value;
      if (failed) failed.hidden = true;
      box.dataset.ambRestockState = 'pending';
      box.setAttribute('aria-busy', 'true');

      fetch('https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' + encodeURIComponent(company), {
        method: 'POST',
        headers: { 'content-type': 'application/json', revision: KLAVIYO_REVISION },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              channels: ['EMAIL'],
              profile: { data: { type: 'profile', attributes: { email: address } } },
            },
            relationships: {
              // Le préfixe est la forme sous laquelle l'intégration Shopify
              // range ses variantes chez Klaviyo. Un identifiant nu répond 404,
              // et un 404 ici veut dire « catalogue non synchronisé », pas
              // « adresse refusée ».
              variant: {
                data: { type: 'catalog-variant', id: '$shopify:::$default:::' + variantId },
              },
            },
          },
        }),
      })
        .then((response) => {
          box.removeAttribute('aria-busy');
          delete box.dataset.ambRestockState;
          if (!response.ok) {
            console.error('[ambinoid] restock Klaviyo', response.status);
            show(failed);
            return;
          }
          // La confirmation prend la place du formulaire : le client a demandé
          // une alerte, pas un abonnement à répétition.
          //
          // L'adresse y est répétée, et c'est la seule des quatre lignes qui ne
          // pouvait pas être écrite d'avance : elle dit « celle-là » à qui vient
          // de la taper sur un téléphone. `textContent`, donc rien à échapper —
          // c'est une saisie du visiteur.
          if (doneEmail) doneEmail.textContent = address;
          form.hidden = true;
          // La rangée a fait son office : le format est retenu, l'alerte est
          // posée, et la proposer encore inviterait à un second abonnement.
          pick.hidden = true;
          show(done);
          tagLocale(address);
        })
        .catch((error) => {
          box.removeAttribute('aria-busy');
          delete box.dataset.ambRestockState;
          console.error('[ambinoid] restock Klaviyo', error);
          show(failed);
        });
    });

    // Une frappe après un refus efface le message : il ne dit plus rien de ce
    // que le client vient d'écrire.
    field.addEventListener('input', function () {
      if (failed && !failed.hidden) failed.hidden = true;
    });

    return reset;
  }

  /* ---------- tiroir de navigation mobile ---------- */
  function initDrawer() {
    const found = /** @type {HTMLElement|null} */ (document.querySelector('[data-amb-drawer]'));
    if (!found || found.dataset.ambReady === '1') return;
    found.dataset.ambReady = '1';
    // Alias explicitement typé : TypeScript ne conserve pas le narrowing d'un
    // `const` à l'intérieur des fonctions déclarées plus bas.
    /** @type {HTMLElement} */
    const drawer = found;

    const openers = /** @type {HTMLElement[]} */ ([...document.querySelectorAll('[data-amb-drawer-open]')]);
    /** @type {Element|null} */
    let lastFocused = null;

    function open() {
      lastFocused = document.activeElement;
      drawer.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      for (const btn of openers) btn.setAttribute('aria-expanded', 'true');
      const focusable = /** @type {HTMLElement|null} */ (drawer.querySelector('input, a, button'));
      if (focusable) focusable.focus();
    }

    function close() {
      drawer.hidden = true;
      document.documentElement.style.overflow = '';
      for (const btn of openers) btn.setAttribute('aria-expanded', 'false');
      if (lastFocused instanceof HTMLElement) lastFocused.focus();
    }

    for (const btn of openers) btn.addEventListener('click', open);
    for (const btn of drawer.querySelectorAll('[data-amb-drawer-close]')) {
      btn.addEventListener('click', close);
    }

    // Groupes dépliables. Le repli initial est posé ici plutôt que dans le
    // Liquid : sans JS aucun groupe ne porte `is-collapsed`, tous les
    // sous-menus restent dépliés et le menu reste entièrement navigable.
    // `aria-expanded` part donc de `true` dans le balisage, et suit la classe.
    for (const node of drawer.querySelectorAll('[data-amb-group]')) {
      if (!(node instanceof HTMLElement)) continue;
      /** @type {HTMLElement} */
      const group = node;
      const toggle = group.querySelector('[data-amb-group-toggle]');
      if (!(toggle instanceof HTMLElement)) continue;

      if (!group.hasAttribute('data-amb-group-open')) {
        group.classList.add('is-collapsed');
        toggle.setAttribute('aria-expanded', 'false');
      }

      toggle.addEventListener('click', function () {
        const collapsed = group.classList.toggle('is-collapsed');
        toggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
      });
    }
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && !drawer.hidden) close();
    });
  }

  /* ---------- feuilles du bas : les refermer en les rabaissant ---------- */

  /**
   * Sous 900 px, le mini-panier, la feuille de filtres et le menu de tri sont
   * tous des feuilles ancrées en bas de l'écran. On les referme en les
   * saisissant par le haut et en les tirant vers le bas — le geste attendu d'une
   * feuille, et le seul qui se fasse au pouce sans viser.
   *
   * Le geste est écrit une fois pour les trois. Chaque feuille se déclare par
   * `data-amb-sheet-drag` sur le panneau qui se déplace et `data-amb-sheet-grab`
   * sur ce qui se saisit — poignée et en-tête. Tout le reste du panneau reste au
   * contenu : sans cela, un doigt posé sur la liste du panier la refermerait au
   * lieu de la faire défiler.
   *
   * Ce que le geste ne fait pas : refermer lui-même. Chaque feuille sait déjà le
   * faire, et à sa manière — le mini-panier rend le focus et annule son bandeau,
   * la feuille de filtres rend le focus au chip qui l'a ouverte. Le geste
   * demande donc, et n'exécute pas.
   *
   * @type {Array<(panel: HTMLElement) => boolean>}
   */
  const sheetClosers = [];

  /**
   * Déclare comment refermer une feuille.
   *
   * Un tableau plutôt qu'un registre par élément : le panneau du mini-panier est
   * remplacé à chaque rafraîchissement, et la section entière peut l'être par
   * l'éditeur de thème. Une clé sur l'élément serait donc périmée au premier
   * changement de quantité, là où la fonction, elle, retrouve le panneau courant.
   *
   * @param {(panel: HTMLElement) => boolean} closer - Vrai s'il a pris ce panneau en charge.
   */
  function registerSheetCloser(closer) {
    sheetClosers.push(closer);
  }

  /**
   * Rend au panneau son état de repos : plus de décalage, plus de transition.
   *
   * `is-grabbed` n'est PAS retirée ici, et c'est le point délicat de tout ce
   * bloc. La classe ne sert qu'à couper l'animation d'entrée, dont le
   * `fill: both` maintient sa dernière image et l'emporte sur un `transform` en
   * ligne — sans elle, le panneau ne suivrait pas le doigt d'un pixel. Or
   * repasser `animation` de `none` à son nom **relance** l'animation : retirer
   * la classe après un retour en place ferait remonter la feuille depuis le bas
   * de l'écran, alors qu'elle n'a jamais cessé d'être à sa place.
   *
   * Elle est donc retirée à l'ouverture, par `releaseSheet` — le seul moment où
   * rejouer l'entrée est précisément ce qu'on veut.
   *
   * @param {HTMLElement} panel - Panneau à reposer.
   */
  function restSheet(panel) {
    panel.style.transition = '';
    panel.style.transform = '';
  }

  /**
   * Remet une feuille dans l'état où son animation d'entrée peut rejouer.
   * À appeler à l'ouverture, avant que le panneau ne soit montré.
   *
   * @param {HTMLElement|null} panel - Panneau rouvert, s'il y en a un.
   */
  function releaseSheet(panel) {
    if (!panel) return;
    panel.classList.remove('is-grabbed');
    restSheet(panel);
  }

  /** Déplacement à partir duquel le doigt traîne plutôt qu'il n'appuie. */
  const DRAG_SLOP = 6;
  /** Part de la hauteur du panneau au-delà de laquelle on referme. */
  const DRAG_CLOSE_RATIO = 0.28;
  /** Vitesse (px/ms) qui referme même sur un court déplacement. */
  const DRAG_FLICK = 0.5;
  /**
   * Déplacement minimal pour qu'une vitesse compte.
   *
   * La vitesse est lue sur le dernier mouvement seul, et un doigt qui se lève
   * bouge toujours un peu : sans ce plancher, une secousse de deux pixels au
   * relâchement refermerait une feuille que personne n'a voulu fermer.
   */
  const DRAG_FLICK_MIN = 24;
  /** Durées des deux retours, en millisecondes. Doivent suivre le CSS. */
  const DRAG_BACK_MS = 200;
  const DRAG_AWAY_MS = 180;

  let sheetDragWired = false;

  /**
   * Câble le geste, une fois pour toute la page.
   *
   * Les écouteurs vivent sur `document` et `window`, jamais sur un panneau :
   * ils survivent donc au remplacement d'une section par l'éditeur de thème, et
   * `init()` peut être rejoué sans les empiler.
   */
  function initSheetDrag() {
    if (sheetDragWired) return;
    sheetDragWired = true;

    /**
     * Geste en cours.
     * @type {{
     *   panel: HTMLElement, pointerId: number, startY: number, offset: number,
     *   lastY: number, lastAt: number, speed: number, height: number, engaged: boolean,
     * }|null}
     */
    let drag = null;
    /** Minuteur de secours du retour en cours, s'il y en a un. */
    let settleTimer = 0;

    /** Le format feuille, celui-là même que pose `@media (max-width: 900px)`. */
    function isSheetLayout() {
      return window.matchMedia('(max-width: 900px)').matches;
    }

    /** @param {HTMLElement} panel - Panneau à refermer. */
    function askClose(panel) {
      for (const closer of sheetClosers) {
        if (closer(panel)) return;
      }
      // Personne ne l'a reconnu : plutôt que de laisser une feuille hors de
      // l'écran, on la remet en place.
      restSheet(panel);
    }

    /**
     * Joue le retour, puis conclut.
     *
     * @param {HTMLElement} panel - Panneau à déplacer.
     * @param {string} to - Valeur de `transform` visée.
     * @param {number} ms - Durée de la transition.
     * @param {() => void} done - Appelé une fois arrivé.
     */
    function settle(panel, to, ms, done) {
      let finished = false;

      /** Conclut une fois, quel que soit le signal arrivé le premier. */
      function conclude() {
        if (finished) return;
        finished = true;
        panel.removeEventListener('transitionend', onEnd);
        if (settleTimer) {
          window.clearTimeout(settleTimer);
          settleTimer = 0;
        }
        done();
      }

      /**
       * `transitionend` remonte depuis les descendants, et une feuille en
       * contient beaucoup. Sans filtrer sur la propriété, la première
       * transition venue conclurait le retour à sa place.
       *
       * @param {TransitionEvent} event - Fin de transition, panneau ou descendant.
       */
      function onEnd(event) {
        if (event.target === panel && event.propertyName === 'transform') conclude();
      }

      panel.addEventListener('transitionend', onEnd);
      panel.style.transition = `transform ${ms}ms cubic-bezier(0.2, 0.7, 0.3, 1)`;
      panel.style.transform = to;

      // Filet. `transitionend` ne se déclenche pas si la valeur visée est celle
      // déjà en place — une feuille tirée jusqu'en bas, précisément — ni si
      // l'onglet passe en arrière-plan.
      settleTimer = window.setTimeout(conclude, ms + 60);
    }

    document.addEventListener('pointerdown', function (event) {
      if (drag || event.button > 0 || !isSheetLayout()) return;
      const target = event.target;
      if (!(target instanceof Element)) return;

      const panel = target.closest('[data-amb-sheet-drag]');
      if (!(panel instanceof HTMLElement) || !target.closest('[data-amb-sheet-grab]')) return;

      const height = panel.getBoundingClientRect().height;
      if (height <= 0) return;

      drag = {
        panel,
        pointerId: event.pointerId,
        startY: event.clientY,
        offset: 0,
        lastY: event.clientY,
        lastAt: event.timeStamp,
        speed: 0,
        height,
        engaged: false,
      };
    });

    window.addEventListener(
      'pointermove',
      function (event) {
        if (!drag || event.pointerId !== drag.pointerId) return;
        const delta = event.clientY - drag.startY;

        if (!drag.engaged) {
          // Vers le haut, ou pas encore assez loin : c'est peut-être un appui
          // sur la croix ou sur « tout effacer », qui doit rester un appui.
          if (delta < DRAG_SLOP) return;
          drag.engaged = true;
          drag.panel.classList.add('is-grabbed');
          drag.panel.style.transition = 'none';
        }

        const dt = event.timeStamp - drag.lastAt;
        if (dt > 0) drag.speed = (event.clientY - drag.lastY) / dt;
        drag.lastY = event.clientY;
        drag.lastAt = event.timeStamp;

        // On ne suit que vers le bas : tirer vers le haut ne décolle pas une
        // feuille qui est déjà contre son plafond.
        drag.offset = Math.max(0, delta);
        drag.panel.style.transform = `translateY(${drag.offset}px)`;
      },
      { passive: true }
    );

    /** @param {PointerEvent} event - Doigt levé, ou geste interrompu. */
    function onRelease(event) {
      if (!drag || event.pointerId !== drag.pointerId) return;
      const { panel, offset, height, speed, engaged } = drag;
      drag = null;
      if (!engaged) return;

      // Deux façons de refermer : aller assez loin, ou aller assez vite. La
      // seconde est ce qui rend le geste utilisable d'une main sur une feuille
      // haute, où le quart de la hauteur est hors de portée du pouce.
      if (offset > height * DRAG_CLOSE_RATIO || (offset > DRAG_FLICK_MIN && speed > DRAG_FLICK)) {
        settle(panel, 'translateY(100%)', DRAG_AWAY_MS, function () {
          askClose(panel);
        });
      } else {
        settle(panel, 'translateY(0px)', DRAG_BACK_MS, function () {
          restSheet(panel);
        });
      }
    }

    window.addEventListener('pointerup', onRelease);
    window.addEventListener('pointercancel', onRelease);
  }

  /* ---------- mini-panier : tiroir de bureau, feuille du bas sur téléphone ---------- */

  /**
   * Préfixe de route de la boutique, `/` ou `/de/` selon la langue servie.
   * Passer par lui garde les requêtes dans la locale courante : construire les
   * URL à la main renverrait un tiroir rendu en anglais sur la boutique
   * allemande.
   *
   * @returns {string}
   */
  function cartRoot() {
    const shopify = window.Shopify;
    const root = shopify && shopify.routes ? shopify.routes.root : null;
    return root || '/';
  }

  /**
   * Vrai si le formulaire est un ajout au panier.
   *
   * `form.action` est toujours absolu une fois lu depuis le DOM, d'où le test
   * sur le chemin plutôt qu'une égalité stricte.
   *
   * @param {HTMLFormElement} form - Formulaire soumis.
   * @returns {boolean}
   */
  function isAddToCartForm(form) {
    try {
      return new URL(form.action, window.location.href).pathname.endsWith('/cart/add');
    } catch (error) {
      return false;
    }
  }

  /**
   * Le tiroir n'est câblé qu'une fois par page : tous les écouteurs vivent sur
   * `document` et relisent la racine à chaque usage. Une re-génération de la
   * section par l'éditeur de thème remplace le noeud sans rien débrancher, et
   * `init()` peut être rejoué sans empiler les écouteurs.
   */
  let cartWired = false;

  /**
   * Aligne le verrou de défilement sur l'état réel du tiroir.
   *
   * Le CSS lit `html.amb-cart-open` là où il lisait
   * `html:has(.amb-cart:not([hidden]))` : un `:has()` ancré sur la racine fait
   * recalculer le style du document entier à chaque nœud inséré n'importe où
   * dans la page — et Shopify en insère une soixantaine après le chargement.
   * La classe rend le verrou aussi fidèle, à condition d'être relue à chaque
   * écriture de `hidden` sur la racine, et à chaque `init()` : l'éditeur de
   * thème peut remplacer la section, tiroir ouvert, par une racine masquée.
   */
  function syncCartLock() {
    const root = document.querySelector('[data-amb-cart]');
    document.documentElement.classList.toggle('amb-cart-open', root instanceof HTMLElement && !root.hidden);
  }

  function initCartDrawer() {
    syncCartLock();
    if (cartWired) return;
    if (!document.querySelector('[data-amb-cart]')) return;
    cartWired = true;

    /**
     * Message à poser dans le bandeau au prochain rafraîchissement.
     * `error: true` réutilise le même emplacement pour un refus du serveur
     * (« il ne reste que 3 exemplaires »), sans icône ni libellé de succès.
     *
     * @type {{ text: string, error: boolean }|null}
     */
    let pendingFlash = null;
    /** Empêche deux mutations concurrentes du panier. */
    let busy = false;
    /** @type {Element|null} */
    let lastFocused = null;

    /** @returns {HTMLElement|null} */
    function rootEl() {
      return /** @type {HTMLElement|null} */ (document.querySelector('[data-amb-cart]'));
    }

    /** @returns {HTMLElement|null} */
    function panelEl() {
      return /** @type {HTMLElement|null} */ (document.querySelector('[data-amb-cart-panel]'));
    }

    /**
     * Seul point d'écriture de `hidden` sur la racine : la visibilité du tiroir
     * et le verrou de défilement bougent ensemble, ou pas du tout.
     *
     * @param {HTMLElement} root - La racine du tiroir.
     * @param {boolean} hidden - Vrai pour masquer.
     */
    function setHidden(root, hidden) {
      root.hidden = hidden;
      syncCartLock();
    }

    /**
     * Durée de l'animation de sortie, en millisecondes. Doit rester alignée
     * sur `.amb-cart.is-closing .amb-cart__panel` dans ambinoid.css. Sert de
     * filet, pas d'horloge : c'est `animationend` qui fait foi.
     */
    const CLOSE_MS = 200;

    /** Minuteur de secours de la fermeture en cours, s'il y en a une. */
    let closeTimer = 0;

    /**
     * Ouvert veut dire « visible et pas en train de partir ».
     *
     * Pendant la sortie la racine n'est pas encore masquée : sans cette
     * seconde condition, Échap relancerait une fermeture déjà lancée et le
     * piège à focus ramènerait le focus dans un panneau qui s'en va.
     */
    function isOpen() {
      const root = rootEl();
      return root !== null && !root.hidden && !root.classList.contains('is-closing');
    }

    /** true si l'internaute a demandé à limiter les animations. */
    function prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    /** Annule une sortie en cours et remet le panneau dans son état d'ouverture. */
    function cancelClose() {
      if (closeTimer) {
        window.clearTimeout(closeTimer);
        closeTimer = 0;
      }
      const root = rootEl();
      if (root) root.classList.remove('is-closing');
    }

    function open() {
      const root = rootEl();
      if (!root || isOpen()) return;

      // Ré-ouverture en pleine sortie : on reprend la main sur l'animation, et
      // `lastFocused` n'est pas réécrit — il pointe déjà sur le déclencheur.
      const wasHidden = root.hidden;
      cancelClose();
      if (wasHidden) lastFocused = document.activeElement;

      // Un panneau qui a été saisi au doigt ne rejoue plus son entrée tant que
      // `is-grabbed` est là. On la retire ici, et nulle part ailleurs : c'est
      // l'ouverture, et elle seule, qui veut cette animation.
      releaseSheet(panelEl());

      setHidden(root, false);
      const closeBtn = /** @type {HTMLElement|null} */ (root.querySelector('.amb-cart__close'));
      if (closeBtn) closeBtn.focus();
    }

    /**
     * Referme le tiroir en jouant l'animation de sortie.
     *
     * La racine n'est masquée qu'une fois l'animation terminée : `hidden`
     * applique `display: none`, qui couperait le panneau net au milieu du
     * mouvement. Le focus, lui, est rendu tout de suite — faire patienter le
     * clavier deux dixièmes de seconde pour une question de décor serait le
     * mauvais arbitrage.
     */
    /**
     * @param {{ animate?: boolean }} [options] - `animate: false` masque sans
     *   jouer la sortie. C'est le cas d'une feuille déjà tirée hors de l'écran
     *   au doigt : rejouer la sortie la ferait remonter d'un coup pour
     *   redescendre aussitôt.
     */
    function close(options) {
      const root = rootEl();
      if (!root || !isOpen()) return;
      pendingFlash = null;
      if (lastFocused instanceof HTMLElement) lastFocused.focus();

      const panel = panelEl();
      if (options && options.animate === false) {
        setHidden(root, true);
        if (panel) releaseSheet(panel);
        return;
      }

      /** Masque pour de bon, si la fermeture n'a pas été annulée entre-temps. */
      function settle() {
        if (panel) panel.removeEventListener('animationend', onAnimationEnd);
        if (closeTimer) {
          window.clearTimeout(closeTimer);
          closeTimer = 0;
        }
        const current = rootEl();
        if (!current || !current.classList.contains('is-closing')) return;
        current.classList.remove('is-closing');
        setHidden(current, true);
      }

      /**
       * `animationend` remonte depuis les descendants. Sans filtrer sur le nom,
       * une animation posée un jour à l'intérieur du panneau conclurait la
       * sortie avant l'heure — et un `{ once: true }` aurait en plus consommé
       * l'écouteur au passage, laissant le vrai signal sans destinataire.
       *
       * @param {AnimationEvent} event - Fin d'animation, panneau ou descendant.
       */
      function onAnimationEnd(event) {
        if (event.animationName === 'amb-cart-slide-out') settle();
      }

      if (prefersReducedMotion()) {
        setHidden(root, true);
        return;
      }

      root.classList.add('is-closing');
      if (panel) panel.addEventListener('animationend', onAnimationEnd);

      // Filet. `animationend` ne se déclenche pas si l'onglet est en arrière-plan,
      // si le panneau a été remplacé entre-temps par l'éditeur de thème, ou si
      // l'animation disparaît du CSS. Sans lui, le tiroir resterait à l'écran.
      closeTimer = window.setTimeout(settle, CLOSE_MS + 60);
    }

    /**
     * Reporte le nombre d'articles sur les pastilles de l'en-tête.
     *
     * L'en-tête est une autre section : la re-rendre entière rejouerait le
     * mégamenu et le sélecteur de langue pour un seul chiffre. La pastille est
     * donc toujours rendue côté serveur, masquée à zéro, et mise à jour ici.
     *
     * @param {string} count - Nombre d'articles, tel que rendu par la section.
     */
    function syncCount(count) {
      for (const el of document.querySelectorAll('[data-amb-cart-count]')) {
        if (!(el instanceof HTMLElement)) continue;
        // Garde-fou : `textContent` remplace tout le sous-arbre par le chiffre.
        // Sur un noeud qui contient le tiroir, cela l'effacerait purement et
        // simplement. `data-amb-cart-count` ne doit marquer qu'une pastille —
        // le compteur de la racine vit sous `data-amb-cart-total`, un nom
        // distinct pour que les deux ne puissent plus se confondre.
        if (el.hasAttribute('data-amb-cart')) continue;
        el.textContent = count;
        el.hidden = count === '0';
      }
    }

    /**
     * Remplit le bandeau du tiroir.
     *
     * @param {Element} panel - Panneau fraîchement rendu.
     * @param {{ text: string, error: boolean }} message - Texte et nature du message.
     */
    function showFlash(panel, message) {
      if (!message.text) return;
      const flash = /** @type {HTMLElement|null} */ (panel.querySelector('[data-amb-cart-flash]'));
      const item = panel.querySelector('[data-amb-cart-flash-item]');
      const label = /** @type {HTMLElement|null} */ (panel.querySelector('[data-amb-cart-flash-label]'));
      const icon = /** @type {SVGElement|null} */ (panel.querySelector('[data-amb-cart-flash-icon]'));
      if (!flash || !item) return;

      flash.classList.toggle('amb-cart__flash--error', message.error);
      if (label) label.hidden = message.error;
      if (icon) icon.style.display = message.error ? 'none' : '';
      item.textContent = message.text;
      flash.hidden = false;
    }

    /**
     * Rejoue la section côté serveur et remplace le contenu du panneau.
     *
     * Seul l'intérieur de `[data-amb-cart-panel]` est échangé : l'attribut
     * `hidden` de la racine reste piloté par le JS, sinon chaque
     * rafraîchissement refermerait le tiroir qu'il vient de mettre à jour.
     *
     * @returns {Promise<void>}
     */
    function refresh() {
      const url = new URL(cartRoot(), window.location.origin);
      url.searchParams.set('sections', 'ambinoid-cart-drawer');

      return fetch(url.toString(), { headers: { Accept: 'application/json' } })
        .then((response) => (response.ok ? response.json() : Promise.reject(response.status)))
        .then((data) => {
          const html = data['ambinoid-cart-drawer'];
          if (typeof html !== 'string') return;

          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const fresh = parsed.querySelector('[data-amb-cart-panel]');
          const current = panelEl();
          const root = rootEl();
          if (!fresh || !current || !root) return;

          current.innerHTML = fresh.innerHTML;

          const freshRoot = parsed.querySelector('[data-amb-cart]');
          const count = freshRoot ? freshRoot.getAttribute('data-amb-cart-total') : null;
          if (count !== null) syncCount(count);

          if (pendingFlash) {
            showFlash(current, pendingFlash);
            pendingFlash = null;
          }

          // `innerHTML` a détruit l'élément qui avait le focus. Sans ce rappel,
          // le focus retombe sur <body> et la navigation au clavier sort du
          // panneau alors que celui-ci est toujours ouvert.
          if (isOpen() && !root.contains(document.activeElement)) {
            const closeBtn = /** @type {HTMLElement|null} */ (current.querySelector('.amb-cart__close'));
            if (closeBtn) closeBtn.focus();
          }
        })
        .catch(() => {
          // Volontairement absorbé, et c'est structurant : un rafraîchissement
          // raté ne doit jamais remonter à l'appelant. Côté ajout au panier, la
          // remontée déclencherait le repli en soumission classique — donc un
          // second ajout du même article, alors que le premier a réussi.
          // L'affichage reste sur l'état précédent, la prochaine mutation le
          // remettra d'aplomb.
        });
    }

    /**
     * Enchaîne une mutation du panier puis le rafraîchissement du tiroir.
     *
     * @param {string} path - Chemin de l'API panier, relatif à la racine de la boutique.
     * @param {object} payload - Corps JSON attendu par cette route.
     * @returns {Promise<void>}
     */
    function mutate(path, payload) {
      const panel = panelEl();
      if (busy) return Promise.resolve();
      busy = true;
      if (panel) panel.setAttribute('aria-busy', 'true');

      return fetch(cartRoot() + path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      })
        .then((response) => (response.ok ? null : response.json().catch(() => null)))
        .then((failure) => {
          // `fetch` ne rejette pas sur un 4xx : un refus de stock arrive ici
          // avec un corps JSON. Le taire laisserait un « + » sans effet visible.
          if (failure) {
            pendingFlash = { text: failure.description || failure.message || '', error: true };
          }
          return refresh();
        })
        .catch(() => {
          /* Le panier reste tel que le serveur le connaît ; le prochain
             rafraîchissement rétablira l'affichage. */
        })
        .finally(() => {
          busy = false;
          const fresh = panelEl();
          if (fresh) fresh.removeAttribute('aria-busy');
        });
    }

    /* --- refermeture au doigt, depuis le haut de la feuille --- */
    registerSheetCloser(function (panel) {
      if (panel !== panelEl()) return false;
      // Sans animation : la feuille est déjà hors de l'écran, tirée par le doigt.
      close({ animate: false });
      return true;
    });

    /* --- ouverture depuis l'en-tête --- */
    document.addEventListener('click', function (event) {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const opener = target.closest('[data-amb-cart-open]');
      if (!opener) return;
      event.preventDefault();
      open();
      refresh();
    });

    /* --- fermeture : voile, croix, Échap --- */
    document.addEventListener('click', function (event) {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('[data-amb-cart-close]')) close();
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && isOpen()) close();
    });

    /* --- piège à focus ---
       `aria-modal="true"` annonce une fenêtre modale aux lecteurs d'écran, mais
       n'empêche pas la tabulation d'en sortir : sur un <div>, seul le JS peut
       tenir cette promesse. */
    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Tab' || !isOpen()) return;
      const panel = panelEl();
      if (!panel) return;

      const focusable = /** @type {HTMLElement[]} */ (
        [...panel.querySelectorAll('a[href], button:not([disabled]), input, select, textarea')].filter(
          // `offsetParent` à null = élément masqué : il ne doit pas capter le focus.
          (el) => el instanceof HTMLElement && el.offsetParent !== null
        )
      );
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      const active = document.activeElement;
      const inside = panel.contains(active);

      if (event.shiftKey && (active === first || !inside)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !inside)) {
        event.preventDefault();
        first.focus();
      }
    });

    /* --- quantité et suppression --- */
    document.addEventListener('click', function (event) {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const line = target.closest('[data-amb-cart-line]');
      if (!line) return;

      const key = line.getAttribute('data-amb-cart-line');
      if (!key) return;

      if (target.closest('[data-amb-cart-remove]')) {
        event.preventDefault();
        mutate('cart/change.js', { id: key, quantity: 0 });
        return;
      }

      const step = target.closest('[data-amb-cart-step]');
      if (!step) return;
      event.preventDefault();

      const value = line.querySelector('[data-amb-cart-qty]');
      const current = parseInt((value && value.textContent) || '', 10);
      if (!current) return;

      const next = current + (parseInt(step.getAttribute('data-amb-cart-step') || '0', 10) || 0);
      // À zéro, /cart/change.js retire la ligne : c'est le comportement attendu
      // du « − » sur le dernier exemplaire, la corbeille restant le geste explicite.
      mutate('cart/change.js', { id: key, quantity: next < 0 ? 0 : next });
    });

    /* --- ajout au panier en arrière-plan --- */
    document.addEventListener('submit', function (event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!isAddToCartForm(form)) return;

      event.preventDefault();
      if (busy) return;
      busy = true;

      // Alias explicitement typé : TypeScript ne conserve pas le narrowing
      // d'`event.target` à l'intérieur de `fallback()`, déclarée plus bas.
      /** @type {HTMLFormElement} */
      const addForm = form;

      /**
       * Boutons d'envoi du formulaire, lus par `elements` et non par
       * `querySelector` : la collection ramène aussi ceux posés hors du
       * formulaire par l'attribut `form`. C'est le cas de la barre d'achat
       * mobile de la fiche produit — soit précisément le bouton « ajouter »
       * du téléphone, qui serait resté actif pendant l'aller-retour.
       *
       * @type {Array<HTMLButtonElement|HTMLInputElement>}
       */
      const submits = [];
      for (const el of Array.from(addForm.elements)) {
        if ((el instanceof HTMLButtonElement || el instanceof HTMLInputElement) && el.type === 'submit') {
          submits.push(el);
        }
      }

      /** @param {boolean} disabled - État à poser sur tous les boutons d'envoi. */
      function setSubmitDisabled(disabled) {
        for (const el of submits) el.disabled = disabled;
      }

      setSubmitDisabled(true);

      /** Rend la main à Shopify : le formulaire repart en navigation classique.
       *  Appelé seulement quand l'ajout AJAX a échoué, donc sans risque de double ajout. */
      function fallback() {
        busy = false;
        setSubmitDisabled(false);
        addForm.submit();
      }

      /**
       * L'article est au panier : on ouvre, on rafraîchit, on rend la main.
       *
       * @param {{ product_title?: string, variant_title?: string }} item - Ligne renvoyée par /cart/add.js.
       * @returns {Promise<void>}
       */
      function onAdded(item) {
        const parts = [];
        if (item && item.product_title) parts.push(item.product_title);
        if (item && item.variant_title && item.variant_title !== 'Default Title') parts.push(item.variant_title);
        pendingFlash = { text: parts.join(', '), error: false };

        const root = rootEl();
        const autoOpen = !root || root.getAttribute('data-amb-cart-auto-open') !== 'false';
        if (autoOpen) open();

        return refresh().then(() => {
          busy = false;
          setSubmitDisabled(false);
        });
      }

      // `then(succès, échec)` et non `then(...).catch(...)` : le repli ne doit
      // écouter que l'échec de l'ajout lui-même. Branché en `catch`, il
      // rattraperait aussi ce qui échoue *après* un ajout réussi et
      // re-soumettrait le formulaire — l'article partirait en double.
      fetch(cartRoot() + 'cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(addForm),
      })
        .then((response) => (response.ok ? response.json() : Promise.reject(response.status)))
        .then(onAdded, fallback);
    });
  }

  /* ---------- barres de filtres : chips-menus, feuille en bas d'écran ---------- */
  /**
   * Câble une barre de filtres : un menu par `[data-amb-filter]`, un seul
   * ouvert à la fois.
   *
   * @param {HTMLElement} bar - Élément portant `data-amb-filters`.
   */
  function initFilters(bar) {
    if (bar.dataset.ambReady === '1') return;
    bar.dataset.ambReady = '1';

    /** @type {{ root: HTMLElement, toggle: HTMLElement, panel: HTMLElement }[]} */
    const menus = [];
    for (const root of bar.querySelectorAll('[data-amb-filter]')) {
      const toggle = root.querySelector('[data-amb-filter-toggle]');
      const panel = root.querySelector('[data-amb-filter-panel]');
      if (root instanceof HTMLElement && toggle instanceof HTMLElement && panel instanceof HTMLElement) {
        menus.push({ root, toggle, panel });
      }
    }
    if (menus.length === 0) return;

    function isSheet() {
      return window.matchMedia('(max-width: 900px)').matches;
    }

    function closeAll() {
      for (const menu of menus) {
        if (menu.panel.hidden) continue;
        menu.panel.hidden = true;
        menu.panel.classList.remove('is-flipped');
        menu.toggle.setAttribute('aria-expanded', 'false');
      }
      document.documentElement.style.overflow = '';
    }

    /** @param {{ root: HTMLElement, toggle: HTMLElement, panel: HTMLElement }} menu */
    function open(menu) {
      closeAll();
      // Voir `releaseSheet` : une feuille saisie au doigt garde `is-grabbed`,
      // qui coupe son animation d'entrée. L'ouverture est le moment de la rendre.
      releaseSheet(menu.panel.querySelector('[data-amb-sheet-drag]'));
      menu.panel.hidden = false;
      menu.toggle.setAttribute('aria-expanded', 'true');

      if (isSheet()) {
        document.documentElement.style.overflow = 'hidden';
      } else {
        // Les derniers chips de la barre ouvriraient leur panneau hors de
        // l'écran : on le raccroche alors au bord droit du chip.
        const box = menu.panel.getBoundingClientRect();
        if (box.right > document.documentElement.clientWidth - 8) {
          menu.panel.classList.add('is-flipped');
        }
      }

      const focusable = menu.panel.querySelector('input:not([type="hidden"]), a[href], button');
      if (focusable instanceof HTMLElement) focusable.focus({ preventScroll: true });
    }

    for (const menu of menus) {
      menu.toggle.addEventListener('click', function () {
        if (menu.panel.hidden) open(menu);
        else closeAll();
      });
      for (const btn of menu.panel.querySelectorAll('[data-amb-filter-close]')) {
        btn.addEventListener('click', function () {
          closeAll();
          menu.toggle.focus({ preventScroll: true });
        });
      }
    }

    /* La feuille se referme aussi en la rabaissant : le geste demande, ce bloc
       exécute — même chemin que le voile ou la poignée, focus compris. */
    registerSheetCloser(function (panel) {
      const menu = menus.find((candidate) => candidate.panel.contains(panel));
      if (!menu || menu.panel.hidden) return false;
      closeAll();
      restSheet(panel);
      menu.toggle.focus({ preventScroll: true });
      return true;
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      for (const menu of menus) {
        if (menu.panel.hidden) continue;
        closeAll();
        menu.toggle.focus({ preventScroll: true });
        return;
      }
    });

    document.addEventListener('click', function (event) {
      const target = event.target;
      if (!(target instanceof Node)) return;
      // Le voile de la feuille mobile est dans le menu : il porte son propre
      // `data-amb-filter-close`, sinon un clic dessus ne fermerait rien.
      for (const menu of menus) {
        if (menu.root.contains(target)) return;
      }
      closeAll();
    });
  }

  /* ---------- curseur de prix ---------- */
  /**
   * Câble un `[data-amb-range]` : un `<input type="range">` pour le prix
   * maximum, un champ de saisie qui dit la même chose, et le remplissage rose
   * du zéro jusqu'à la borne.
   *
   * C'est le champ qui fait foi : lui seul porte le `name` du filtre, lui seul
   * accepte les centimes. Le curseur le pilote.
   *
   * Le rose est la largeur d'un `div` — `[data-amb-range-fill]` — et non le
   * track natif de l'input : voir le commentaire du bloc `.amb-range` dans
   * `ambinoid.css` pour ce qui a mené là.
   *
   * @param {HTMLElement} root - Élément portant `data-amb-range`.
   */
  function initRange(root) {
    if (root.dataset.ambReady === '1') return;
    const inputEl = root.querySelector('[data-amb-range-input]');
    const fieldEl = root.querySelector('[data-amb-range-field]');
    const fillEl = root.querySelector('[data-amb-range-fill]');
    if (!(inputEl instanceof HTMLInputElement) || !(fieldEl instanceof HTMLInputElement)) return;
    if (!(fillEl instanceof HTMLElement)) return;
    root.dataset.ambReady = '1';

    // La feuille mobile n'a pas de case de saisie : elle affiche la borne en
    // clair au-dessus de la piste, et son champ est caché. Facultatif — les
    // menus du bureau montrent la même valeur dans le champ lui-même.
    const outEl = root.querySelector('[data-amb-range-out]');
    /** @type {HTMLElement|null} */
    const out = outEl instanceof HTMLElement ? outEl : null;

    /** @type {HTMLInputElement} */
    const input = inputEl;
    /** @type {HTMLInputElement} */
    const field = fieldEl;
    /** @type {HTMLElement} */
    const fill = fillEl;
    /** Diamètre de la poignée, en pixels — il vaut celui du CSS. */
    const THUMB = 15;

    // L'échelle vient du serveur : un `max` vide ou nil rendrait `Number()` à 0
    // et toute l'échelle s'effondrerait — borne écrasée, remplissage nul.
    // Mieux vaut une échelle de repli que pas de curseur du tout.
    const floor = Number(input.min) || 0;
    let ceiling = Number(input.max);
    if (!Number.isFinite(ceiling) || ceiling <= floor) ceiling = floor + 1;
    const span = ceiling - floor;

    /**
     * @param {number} value - Valeur à ramener dans l'échelle.
     * @returns {number}
     */
    function clamp(value) {
      if (value < floor) return floor;
      if (value > ceiling) return ceiling;
      return value;
    }

    /**
     * Lit le champ. Vide vaut « pas de borne », donc le plafond : sans quoi
     * effacer le champ ramènerait le filtre à zéro produit.
     *
     * La valeur lue n'est pas bornée ici : c'est `set()`, ou le cadrage initial
     * juste en dessous, qui la ramène dans l'échelle.
     *
     * @param {number} fallback - Valeur quand le champ est vide ou illisible.
     * @returns {number}
     */
    function read(fallback) {
      const raw = field.value.trim();
      if (raw === '') return fallback;
      const parsed = Number(raw);
      return Number.isFinite(parsed) ? parsed : fallback;
    }

    // La borne vient du serveur : rien ne garantit qu'elle tienne dans l'échelle
    // rendue juste à côté. Hors échelle, elle donnerait un remplissage négatif
    // ou plus large que la piste — du rose partout, ou pas de rose du tout.
    let high = clamp(read(ceiling));

    /**
     * Reporte l'état sur le curseur, le champ et la piste.
     *
     * @param {HTMLInputElement|null} typing - Champ en cours de saisie, qu'on
     *   ne réécrit pas sous les doigts de l'utilisateur.
     */
    function paint(typing) {
      // Le curseur avance par unités entières : une borne à 12,50 € place la
      // poignée sur la graduation la plus proche, tandis que le remplissage et
      // le champ gardent le centime.
      input.value = String(high);
      if (field !== typing) field.value = high === ceiling ? '' : String(high);
      // La borne en clair, elle, dit toujours un nombre : le champ se vide au
      // plafond parce qu'une borne qui ne filtre rien n'a pas à être postée,
      // mais un « Prix max : » suivi de rien ne se lit pas.
      if (out) out.textContent = String(high);

      // La poignée garde une demi-largeur de marge à chaque bout de la course,
      // pour y tenir entière : son centre n'est donc pas à `f %` de la piste,
      // mais à `(7,5 + f × (largeur − 15)) / largeur`. Sans ce décalage le rose
      // s'arrête à côté de la poignée plutôt que sous elle.
      //
      // La part est bornée à [0, 1] avant d'être écrite : hors de là, elle ne
      // pourrait ni déborder ni — pire — rendre la déclaration caduque et
      // effacer le rose sans que rien ne le signale.
      let fraction = (high - floor) / span;
      if (!Number.isFinite(fraction)) fraction = 1;
      fraction = Math.min(1, Math.max(0, fraction));

      // Panneau encore fermé : pas de largeur à mesurer, on s'en tient à la part
      // brute. Le ResizeObserver repassera dès qu'il s'ouvre.
      const width = input.clientWidth;
      const filled = width > THUMB ? ((THUMB / 2 + fraction * (width - THUMB)) / width) * 100 : fraction * 100;

      // Les deux couches de la piste, posées ensemble : la largeur du `div`, et
      // la taille du fond de l'input. Elles se superposent au pixel près, et
      // chacune seule suffit à rendre la piste — voir le commentaire du bloc
      // `.amb-range` dans ambinoid.css pour ce qui a mené là.
      fill.style.width = filled + '%';
      input.style.setProperty('--amb-range-filled', filled + '%');
    }

    /**
     * Pose la borne.
     *
     * @param {number} value - Sa nouvelle valeur.
     * @param {HTMLInputElement|null} typing - Champ en cours de saisie.
     */
    function set(value, typing) {
      high = clamp(value);
      paint(typing);
    }

    input.addEventListener('input', function () {
      set(Number(input.value), null);
    });

    // `input` suit la frappe pour que la poignée bouge en direct ; `change`
    // (sortie du champ, entrée) normalise ce qui a été tapé — « 12,, », « 900 »
    // sur une échelle qui s'arrête à 50.
    field.addEventListener('input', function () {
      set(read(ceiling), field);
    });
    field.addEventListener('change', function () {
      set(read(ceiling), null);
    });

    const form = root.closest('form');
    if (form) {
      form.addEventListener('submit', function () {
        // Une borne restée au plafond ne filtre rien : la poster n'allongerait
        // l'URL que pour allumer le chip sur un filtre qui ne retire aucun
        // produit. Un champ désactivé n'est pas sérialisé.
        field.disabled = high >= ceiling;
        // La page suivante peut venir du cache de navigation, avec ce DOM-ci :
        // on rend la main au champ dès la soumission partie.
        window.setTimeout(function () {
          field.disabled = false;
        }, 0);
      });
    }

    // Le curseur est peint alors que son menu est encore `hidden` : sans
    // largeur, le décalage de poignée ne peut pas être calculé. On repeint donc
    // dès que la piste en a une.
    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(function () {
        paint(null);
      }).observe(input);
    }

    paint(null);
  }

  /* ---------- feuille de filtres mobile ---------- */
  /**
   * Câble le bouton d'envoi d'une feuille de filtres.
   *
   * Le serveur rend « Voir 12 produits » : c'est le compte des filtres déjà
   * appliqués, tout ce qu'il sait au moment du rendu. Dès qu'une case bouge
   * dans la feuille, ce chiffre ne dit plus ce que l'envoi rapportera — le
   * savoir demanderait d'aller le chercher, et le bouton mentirait en
   * attendant. Il passe donc à un libellé sans chiffre, et retrouve le compte
   * si la sélection revient à son état de départ.
   *
   * @param {HTMLElement} root - Élément portant `data-amb-sheet`.
   */
  function initSheet(root) {
    if (root.dataset.ambReady === '1') return;
    if (!(root instanceof HTMLFormElement)) return;
    const buttonEl = root.querySelector('[data-amb-sheet-submit]');
    if (!(buttonEl instanceof HTMLElement)) return;
    root.dataset.ambReady = '1';

    /** @type {HTMLFormElement} */
    const form = root;
    /** @type {HTMLElement} */
    const button = buttonEl;
    const counted = button.textContent || '';
    const pending = button.dataset.ambSheetPending || counted;

    /**
     * Ce que la feuille posterait en l'état.
     *
     * `FormData` plutôt qu'une lecture des cases : c'est exactement ce que
     * l'envoi emportera, y compris le champ caché du prix que `initRange()`
     * réécrit sous nos pieds.
     *
     * @returns {string}
     */
    function state() {
      const parts = [];
      for (const [name, value] of new FormData(form)) parts.push(name + '=' + String(value));
      return parts.join('&');
    }

    // L'état de départ se lit après `initRange()`, qui normalise la borne du
    // prix au premier rendu : le lire avant ferait paraître modifiée une
    // feuille que personne n'a touchée.
    const applied = state();

    function refresh() {
      button.textContent = state() === applied ? counted : pending;
    }

    form.addEventListener('input', refresh);
    form.addEventListener('change', refresh);
  }

  /* ---------- sommaire : construction et suivi du défilement ---------- */
  /**
   * Construit un sommaire dans chacun des conteneurs fournis, à partir des
   * titres du corps. Les ancres manquent à l'appel : ni l'éditeur rich text de
   * l'admin ni l'import de contenu ne posent d'`id`, il faut donc les dériver.
   *
   * h2 ET h3 : le contenu importé dans la boutique structure ses articles en
   * h3, un sommaire limité aux h2 y resterait vide. Le niveau est reporté sur
   * le lien pour que le CSS décale les sous-titres.
   *
   * Plusieurs conteneurs pour une même page : la page légale en rend un par
   * point de rupture — colonne collée en bureau, feuille dépliable en mobile —
   * et le CSS montre celui qui a cours. Ils reçoivent donc la même liste.
   *
   * Un conteneur peut arriver déjà rempli : la section article rend son
   * sommaire en Liquid, pour qu'il soit là dès le premier rendu au lieu
   * d'apparaître après coup et de pousser le corps (un décalage de 0,055 sur
   * téléphone, mesuré). On l'adopte alors plutôt que de le reconstruire : le
   * i-ème lien va au i-ème titre, dont il fournit l'`id` — c'est Liquid qui a
   * fabriqué l'ancre, le titre n'en avait pas — et qui garde le sien s'il en
   * avait un. L'adoption ne vaut que si les comptes concordent, dans chaque
   * conteneur : Liquid découpe du texte, ce script lit le DOM, et un titre
   * écrit `<H2>` n'est vu que d'un côté. Au moindre écart on repart de zéro,
   * conteneurs vidés — jamais deux sommaires, jamais de lien orphelin.
   *
   * @param {Element} body - Élément contenant les titres à lister.
   * @param {Element[]} containers - Conteneurs à remplir, ou à adopter.
   * @returns {{ link: HTMLAnchorElement, heading: HTMLElement }[]} Un couple par lien.
   */
  function buildToc(body, containers) {
    /** @type {{ link: HTMLAnchorElement, heading: HTMLElement }[]} */
    const entries = [];
    if (!containers.length) return entries;

    const headings = /** @type {HTMLElement[]} */ ([...body.querySelectorAll('h2, h3')]);

    const prerendered = containers.map(
      (container) => /** @type {HTMLAnchorElement[]} */ ([...container.querySelectorAll('a[data-amb-level]')])
    );
    const adoptable = headings.length > 0 && prerendered.every((links) => links.length === headings.length);
    if (adoptable) {
      headings.forEach(function (heading, index) {
        for (const links of prerendered) {
          const link = links[index];
          if (!link) continue;
          if (!heading.id) {
            // `hash` est encodé pour l'URL : « über » y devient « %C3%BCber ».
            let wanted = link.hash.slice(1);
            try {
              wanted = decodeURIComponent(wanted);
            } catch (error) {
              // Une ancre mal encodée : on la garde telle quelle.
            }
            heading.id = wanted || 'section-' + (index + 1);
          }
          if (link.getAttribute('href') !== '#' + heading.id) link.setAttribute('href', '#' + heading.id);
          entries.push({ link: link, heading: heading });
        }
      });
      return entries;
    }
    for (const container of containers) container.textContent = '';

    headings.forEach(function (heading, index) {
      if (!heading.id) {
        const slug = (heading.textContent || '')
          .trim()
          .toLowerCase()
          // les articles allemands titrent avec des diacritiques et des « & » :
          // sans translittération le slug se réduirait à une suite de tirets.
          .replace(/[àâäá]/g, 'a')
          .replace(/[éèêë]/g, 'e')
          .replace(/[îï]/g, 'i')
          .replace(/[ôö]/g, 'o')
          .replace(/[ûüù]/g, 'u')
          .replace(/ß/g, 'ss')
          .replace(/&/g, '-')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
        heading.id = slug || 'section-' + (index + 1);
      }
      for (const container of containers) {
        const link = document.createElement('a');
        link.href = '#' + heading.id;
        link.textContent = (heading.textContent || '').trim();
        link.dataset.ambLevel = heading.tagName === 'H3' ? '3' : '2';
        container.appendChild(link);
        entries.push({ link: link, heading: heading });
      }
    });
    return entries;
  }

  /**
   * Marque le titre en cours de lecture. L'état se décide sur le titre, pas sur
   * le lien : un même titre peut être listé dans plusieurs conteneurs, et tous
   * doivent s'allumer ensemble.
   *
   * @param {{ link: HTMLAnchorElement, heading: HTMLElement }[]} entries - Retour de `buildToc`.
   */
  function syncToc(entries) {
    const first = entries[0];
    if (!first) return;
    let current = first.heading;
    for (const entry of entries) {
      if (entry.heading.getBoundingClientRect().top <= 120) current = entry.heading;
    }
    for (const entry of entries) {
      entry.link.classList.toggle('is-active', entry.heading === current);
    }
  }

  /* ---------- article de blog : barre de progression + sommaire ---------- */
  function initArticle() {
    const found = /** @type {HTMLElement|null} */ (document.querySelector('[data-amb-article]'));
    if (!found || found.dataset.ambReady === '1') return;
    found.dataset.ambReady = '1';
    /** @type {HTMLElement} */
    const article = found;

    // Au-dessus de 990px, Horizon fait de .page-wrapper le conteneur de
    // défilement : ce n'est donc pas toujours la fenêtre qui scrolle.
    const scrollEl = article.closest('.page-wrapper');
    /** @type {Element|Window} */
    const scroller = scrollEl || window;

    function viewportHeight() {
      return scrollEl ? scrollEl.clientHeight : window.innerHeight;
    }
    function scrollerTop() {
      return scrollEl ? scrollEl.getBoundingClientRect().top : 0;
    }

    const bar = /** @type {HTMLElement|null} */ (article.querySelector('[data-amb-progress-bar]'));
    const body = article.querySelector('[data-amb-article-body]');
    const toc = article.querySelector('[data-amb-toc]');
    const links = toc && body ? buildToc(body, [toc]) : [];

    // Le CSS masque la carte du sommaire tant que ce drapeau est absent : un
    // article sans <h2> ne doit pas laisser une carte vide dans la colonne.
    if (links.length) article.setAttribute('data-amb-has-toc', '');

    // « Related reading » : sous 700px la grille devient un rail horizontal.
    // La jauge reflète la position réelle du défilement, elle n'est pas un décor.
    const carousel = /** @type {HTMLElement|null} */ (article.querySelector('[data-amb-carousel]'));
    const track = /** @type {HTMLElement|null} */ (article.querySelector('[data-amb-carousel-track]'));
    const thumb = /** @type {HTMLElement|null} */ (article.querySelector('[data-amb-carousel-thumb]'));

    function syncCarousel() {
      if (!carousel || !track || !thumb) return;

      const visible = track.clientWidth;
      const overflow = track.scrollWidth - visible;
      // 4px de tolérance : les arrondis sous-pixel suffisent à faire déborder
      // une grille qui tient pourtant dans la largeur. `visible` vaut 0 quand la
      // section est repliée dans l'éditeur — il n'y a alors rien à mesurer.
      if (visible === 0 || overflow <= 4) {
        carousel.dataset.ambOverflow = '0';
        return;
      }

      carousel.dataset.ambOverflow = '1';
      const ratio = visible / track.scrollWidth;
      // Le curseur se déplace dans la piste : sa course, exprimée en pourcentage
      // de sa propre largeur, vaut (1 - ratio) / ratio.
      const travel = (track.scrollLeft / overflow) * (100 / ratio - 100);
      thumb.style.width = (ratio * 100).toFixed(2) + '%';
      thumb.style.transform = 'translateX(' + travel.toFixed(2) + '%)';
    }

    if (track) track.addEventListener('scroll', syncCarousel, { passive: true });

    function update() {
      if (bar) {
        const rect = article.getBoundingClientRect();
        const travel = rect.height - viewportHeight();
        const progress = travel > 0 ? (scrollerTop() - rect.top) / travel : 0;
        bar.style.width = Math.min(100, Math.max(0, progress * 100)) + '%';
      }

      syncToc(links);
    }

    let ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        update();
        ticking = false;
      });
    }

    scroller.addEventListener('scroll', onScroll, { passive: true });
    if (scrollEl) window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () {
      update();
      // Volontairement hors de `update()` : mesurer scrollWidth force un calcul
      // de mise en page, à ne pas payer à chaque image de défilement.
      syncCarousel();
    });
    update();
    syncCarousel();
  }

  /* ---------- page légale : sommaire + tableaux défilants ---------- */
  /**
   * Le corps du document vient de la page Shopify : c'est du HTML d'éditeur
   * rich text, sans ancre ni conteneur de défilement. Les deux se posent ici.
   *
   * @param {HTMLElement} root - Élément portant `data-amb-legal`.
   */
  function initLegal(root) {
    if (root.dataset.ambReady === '1') return;
    root.dataset.ambReady = '1';

    const body = root.querySelector('[data-amb-legal-body]');
    if (!body) return;

    // Au-dessus de 990px, Horizon fait de .page-wrapper le conteneur de
    // défilement : ce n'est donc pas toujours la fenêtre qui scrolle.
    const scrollEl = root.closest('.page-wrapper');
    /** @type {Element|Window} */
    const scroller = scrollEl || window;

    const entries = buildToc(body, [...root.querySelectorAll('[data-amb-legal-toc]')]);
    // Le CSS masque les deux conteneurs tant que ce drapeau est absent : une page
    // sans <h2> ne doit pas laisser l'intitulé du sommaire tout seul.
    if (entries.length) root.setAttribute('data-amb-has-toc', '');

    // Un tableau saisi dans l'admin arrive nu : il déborde de la carte au lieu
    // d'y défiler. On lui donne le conteneur qui lui manque, focusable pour que
    // le clavier puisse le parcourir.
    const hint = body.getAttribute('data-amb-swipe') || '';
    /** @type {{ wrap: HTMLElement, note: HTMLElement }[]} */
    const tables = [];
    for (const table of body.querySelectorAll('table')) {
      const parent = table.parentNode;
      if (!parent) continue;

      const wrap = document.createElement('div');
      wrap.className = 'amb-legal__table';
      wrap.tabIndex = 0;
      const note = document.createElement('div');
      note.className = 'amb-legal__swipe';
      note.textContent = hint;
      note.hidden = true;

      parent.insertBefore(note, table);
      parent.insertBefore(wrap, table);
      wrap.appendChild(table);
      tables.push({ wrap: wrap, note: note });
    }

    /**
     * L'invitation à faire glisser ne s'affiche que si le tableau déborde
     * vraiment : annoncer un défilement qui n'existe pas vaut moins que rien.
     * 4px de tolérance, les arrondis sous-pixel suffisant à faire déborder un
     * tableau qui tient pourtant dans la largeur.
     */
    function syncTables() {
      for (const entry of tables) {
        entry.note.hidden = hint === '' || entry.wrap.scrollWidth - entry.wrap.clientWidth <= 4;
      }
    }

    let ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        syncToc(entries);
        ticking = false;
      });
    }

    if (entries.length) {
      scroller.addEventListener('scroll', onScroll, { passive: true });
      if (scrollEl) window.addEventListener('scroll', onScroll, { passive: true });
      syncToc(entries);
    }
    // Mesurer scrollWidth force un calcul de mise en page : à payer au
    // redimensionnement, pas à chaque image de défilement.
    window.addEventListener('resize', syncTables);
    syncTables();
  }

  /* ---------- produits liés : remplace le repli par les vraies recommandations ---------- */
  /**
   * @param {HTMLElement} root - Élément portant `data-amb-related` et `data-url`.
   */
  function initRelated(root) {
    if (root.dataset.ambReady === '1') return;
    root.dataset.ambReady = '1';

    const url = root.getAttribute('data-url');
    if (!url) return;

    fetch(url)
      .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
      .then((text) => {
        const parsed = document.createElement('div');
        parsed.innerHTML = text;
        const fresh = parsed.querySelector('[data-amb-related]');
        if (fresh && fresh.innerHTML.trim().length > 0) root.innerHTML = fresh.innerHTML;
      })
      .catch(() => {
        /* on conserve le repli rendu côté serveur */
      });
  }

  /* ---------- produits récemment consultés ---------- */
  /**
   * Ce que la page retient des fiches vues : identifiant et handle, le plus
   * récent d'abord, sans doublon. Rien d'autre — ni titre, ni prix, ni image :
   * la carte est redemandée à la boutique à chaque affichage, donc toujours
   * dans la langue de la page, au prix et au stock du jour.
   *
   * L'identifiant fait l'unicité, le handle sert l'adresse. Les deux ne vont pas
   * l'un sans l'autre ici : un taux de cannabinoïdes figure dans les handles
   * et change d'un lot à l'autre, donc une même fiche peut changer d'adresse.
   * Revisitée, elle remplace sa propre entrée au lieu d'en ajouter une.
   */
  const RECENT_KEY = 'ambinoid:recently-viewed';
  /** Entrées retenues au plus. La section en montre moins : de quoi remplacer les épuisés. */
  const RECENT_KEEP = 10;

  /** @returns {AmbRecentEntry[]} */
  function readRecent() {
    try {
      const data = JSON.parse(window.localStorage.getItem(RECENT_KEY) || '[]');
      if (!Array.isArray(data)) return [];
      return data.filter(
        (entry) => entry && typeof entry.id === 'string' && entry.id && typeof entry.handle === 'string' && entry.handle
      );
    } catch (error) {
      return [];
    }
  }

  /** @param {AmbRecentEntry[]} list - La liste à retenir, déjà ordonnée. */
  function writeRecent(list) {
    try {
      window.localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_KEEP)));
    } catch (error) {
      // Navigation privée ou stockage plein : la page n'en sait simplement rien.
    }
  }

  function forgetRecent() {
    try {
      window.localStorage.removeItem(RECENT_KEY);
    } catch (error) {
      // Rien à effacer qu'on puisse atteindre.
    }
  }

  /**
   * Ce que l'événement `visitorConsentCollected` ou `currentVisitorConsent()`
   * dit de la catégorie « Präferenzen ». Les deux formes, comme pour la carte :
   * `null` veut dire « rien de collecté », pas « refusé ».
   *
   * @param {*} consent - Objet de consentement, quelle que soit sa forme.
   * @returns {boolean|null}
   */
  function readPreferenceConsent(consent) {
    if (!consent || typeof consent !== 'object') return null;
    const value = 'preferencesAllowed' in consent ? consent.preferencesAllowed : consent.preferences;
    if (value === true || value === 'yes') return true;
    if (value === false || value === 'no') return false;
    return null;
  }

  /**
   * Le droit de se souvenir des fiches vues.
   *
   * Retenir ce qu'un visiteur a regardé, d'une visite à l'autre, n'est pas
   * indispensable à la page qu'il demande : sur une boutique allemande c'est
   * l'accord « Präferenzen » de la bannière (§ 25 TDDDG), dont la note le dit.
   * Trois sources, dans cet ordre :
   *
   *   - la réponse que la bannière du thème a retenue. C'est la réponse du
   *     visiteur, et elle survit à un `setTrackingConsent()` perdu, que Shopify
   *     ne saurait plus rapporter (voir `initCookieBanner()`) ;
   *   - `preferencesProcessingAllowed()`, qui répond aussi pour un visiteur
   *     d'une région où la boutique ne demande rien — vrai, alors ;
   *   - `currentVisitorConsent()`, à défaut.
   *
   * Sans l'API ni réponse retenue : non. Ne rien retenir est le seul défaut
   * qu'on n'ait jamais à justifier.
   *
   * @param {AmbCustomerPrivacy|null} privacy - L'API, si la boutique la sert.
   * @returns {boolean}
   */
  function recentAllowed(privacy) {
    const decided = storedConsent();
    if (decided) return decided.preferences === true;
    if (!privacy) return false;
    if (typeof privacy.preferencesProcessingAllowed === 'function') {
      return privacy.preferencesProcessingAllowed() === true;
    }
    if (typeof privacy.currentVisitorConsent === 'function') {
      return readPreferenceConsent(privacy.currentVisitorConsent()) === true;
    }
    return false;
  }

  /**
   * « Zuletzt angesehen » / « Recently viewed ».
   *
   * La section a deux rôles, et c'est le même fichier qui les tient :
   *
   *   - sur une fiche, elle porte l'identifiant et le handle du produit
   *     (`data-amb-recent-current`, `data-amb-recent-handle`), que ce script
   *     range en tête de la liste ;
   *   - partout, elle montre les fiches vues avant, celle de la page exclue.
   *
   * Chaque carte est demandée à la boutique par l'API de rendu de sections, sur
   * l'adresse du produit et sous le nom de fichier de cette même section, que
   * le Liquid pose dans `data-amb-recent` :
   *
   *     GET {racine}products/{handle}?section_id=ambinoid-recently-viewed
   *
   * La réponse est la section rendue pour ce produit-là, qui porte sa propre
   * carte dans un `<template data-amb-recent-card>` — vide quand le produit est
   * épuisé, et c'est le Liquid qui en décide. Une requête par carte, lancées
   * ensemble ; aucune avant que la section n'approche de l'écran.
   *
   * La section reste `hidden` tant qu'aucune carte n'est arrivée : un premier
   * visiteur n'en voit ni le titre ni la place.
   *
   * Une fiche disparue — produit supprimé, ou handle changé sans redirection —
   * est retirée de la liste. Elle ne se reconnaît **pas** à un 404 : relevé sur
   * la boutique le 08/10/2026, `products/{handle inconnu}?section_id=…` répond
   * 200, avec la section rendue sans produit. C'est donc l'absence de
   * `data-amb-recent-current` dans la réponse qui le dit. Un 404, lui, ne vient
   * que d'une section introuvable — un fichier renommé —, et vider l'historique
   * de tout le monde pour cela serait la mauvaise réponse : il est gardé, comme
   * pour tout autre échec. Un réseau qui hoquette n'efface rien.
   *
   * @param {HTMLElement} root - Élément portant `data-amb-recent`.
   */
  function initRecent(root) {
    if (root.dataset.ambReady === '1') return;
    root.dataset.ambReady = '1';

    const sectionName = root.getAttribute('data-amb-recent') || '';
    const grid = root.querySelector('[data-amb-recent-grid]');
    const currentId = root.getAttribute('data-amb-recent-current') || '';
    const currentHandle = root.getAttribute('data-amb-recent-handle') || '';
    const max = parseInt(root.getAttribute('data-amb-recent-max') || '', 10) || 4;
    if (!sectionName || !(grid instanceof HTMLElement)) return;

    let requested = false;

    /**
     * Range la fiche de la page en tête, puis montre les autres.
     *
     * @param {boolean} allowed - Le visiteur a-t-il accepté qu'on s'en souvienne ?
     */
    function apply(allowed) {
      if (!root.isConnected) return;
      if (!allowed) {
        forgetRecent();
        root.hidden = true;
        return;
      }
      const others = readRecent().filter((entry) => entry.id !== currentId);
      if (currentId && currentHandle) writeRecent([{ id: currentId, handle: currentHandle }, ...others]);
      if (requested) return;
      requested = true;
      whenNear(() => show(others));
    }

    /**
     * Diffère le travail jusqu'à ce que la section approche de l'écran.
     *
     * On observe son parent, pas elle : une section `hidden` n'a pas de boîte,
     * et un `IntersectionObserver` ne la verrait jamais entrer. Le conteneur
     * que Shopify pose autour d'elle en a une, même de hauteur nulle.
     *
     * @param {() => void} run - Ce qu'on fait une fois la section proche.
     */
    function whenNear(run) {
      const target = root.parentElement;
      if (!target || typeof IntersectionObserver !== 'function') {
        run();
        return;
      }
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          run();
        },
        { rootMargin: '400px' }
      );
      observer.observe(target);
    }

    /**
     * @param {AmbRecentEntry} entry - La fiche à demander.
     * @returns {Promise<{ entry: AmbRecentEntry, gone: boolean, card: DocumentFragment|null }>}
     */
    function fetchCard(entry) {
      const url = new URL(cartRoot() + 'products/' + encodeURIComponent(entry.handle), window.location.origin);
      url.searchParams.set('section_id', sectionName);
      return fetch(url.toString())
        .then((response) => {
          if (!response.ok) return { entry, gone: false, card: null };
          return response.text().then((html) => {
            const parsed = new DOMParser().parseFromString(html, 'text/html');
            const section = parsed.querySelector('[data-amb-recent]');
            if (section && !section.hasAttribute('data-amb-recent-current')) {
              return { entry, gone: true, card: null };
            }
            const template = parsed.querySelector('template[data-amb-recent-card]');
            const card =
              template instanceof HTMLTemplateElement && template.content.childElementCount > 0
                ? /** @type {DocumentFragment} */ (document.importNode(template.content, true))
                : null;
            return { entry, gone: false, card };
          });
        })
        .catch(() => ({ entry, gone: false, card: null }));
    }

    /** @param {AmbRecentEntry[]} others - Les fiches vues, celle de la page exclue. */
    function show(others) {
      // Deux de plus que ce qu'on montre : de quoi remplacer un épuisé sans
      // aller chercher une seconde vague.
      const wanted = others.slice(0, max + 2);
      if (!wanted.length) return;

      Promise.all(wanted.map(fetchCard)).then((results) => {
        const gone = results.filter((result) => result.gone).map((result) => result.entry.id);
        if (gone.length) writeRecent(readRecent().filter((entry) => !gone.includes(entry.id)));

        if (!root.isConnected || !(grid instanceof HTMLElement)) return;
        const cards = results
          .map((result) => result.card)
          .filter((card) => card !== null)
          .slice(0, max);
        if (!cards.length) return;
        grid.replaceChildren(.../** @type {DocumentFragment[]} */ (cards));
        root.hidden = false;
      });
    }

    document.addEventListener('visitorConsentCollected', (event) => {
      // Même garde que la carte : l'écouteur survit à un re-rendu de la
      // section dans l'éditeur de thème, et vise alors un noeud détaché.
      if (!root.isConnected) return;
      const allowed = readPreferenceConsent(/** @type {CustomEvent} */ (event).detail);
      if (allowed !== null) apply(allowed);
    });

    withCustomerPrivacy().then((privacy) => apply(recentAllowed(privacy)));
  }

  /* ---------- Description produit repliable ---------- */
  /**
   * Le plafond de la description est en CSS, dès le premier rendu : ce script
   * ne fait que dire s'il mord. Un texte qui déborde reçoit le fondu et le
   * bouton, posé en surimpression sur la dernière ligne — rien ne change de
   * hauteur à son arrivée. Un texte qui tient reste tel quel, sans bouton.
   *
   * La mesure est reprise quand la largeur change (rotation, passage des
   * 900px, où le plafond passe de huit lignes à cinq) et une fois les polices
   * arrivées, qui changent la hauteur du texte sans changer celle de la boîte
   * plafonnée.
   *
   * @param {HTMLElement} root - Élément portant `data-amb-more`.
   */
  function initMore(root) {
    if (root.dataset.ambReady === '1') return;
    const foundBody = root.querySelector('[data-amb-more-body]');
    const foundToggle = root.querySelector('[data-amb-more-toggle]');
    if (!(foundBody instanceof HTMLElement) || !(foundToggle instanceof HTMLButtonElement)) return;
    /** @type {HTMLElement} */
    const body = foundBody;
    /** @type {HTMLButtonElement} */
    const toggle = foundToggle;
    root.dataset.ambReady = '1';

    const labelMore = toggle.dataset.labelMore || (toggle.textContent || '').trim();
    const labelLess = toggle.dataset.labelLess || labelMore;

    function measure() {
      if (root.classList.contains('amb-more--open')) return;
      // Un pixel de marge : l'arrondi sous-pixel de `lh` poserait sinon un
      // bouton sur un texte qui tient.
      const clamped = body.scrollHeight > body.clientHeight + 1;
      root.classList.toggle('amb-more--clamped', clamped);
      toggle.hidden = !clamped;
    }

    toggle.addEventListener('click', () => {
      const open = !root.classList.contains('amb-more--open');
      root.classList.toggle('amb-more--open', open);
      root.classList.toggle('amb-more--clamped', !open);
      toggle.setAttribute('aria-expanded', String(open));
      toggle.textContent = open ? labelLess : labelMore;
      // Replier depuis le bas d'un long texte laisserait la fenêtre loin sous
      // une description redevenue courte : on la ramène en vue.
      if (!open && root.getBoundingClientRect().top < 0) root.scrollIntoView({ block: 'start' });
    });

    measure();
    new ResizeObserver(measure).observe(root);
    if (document.fonts) document.fonts.ready.then(measure, () => {});
  }

  /* ---------- FAQ : ouverture et fermeture animées ---------- */
  /**
   * Anime la hauteur du panneau d'une question sans toucher à la sémantique de
   * `<details>` : le clic sur le `<summary>` est intercepté, l'attribut `open`
   * n'est retiré qu'à la fin de l'animation de fermeture. Sans ce script — ou
   * en mouvement réduit — l'accordéon fonctionne toujours, il s'ouvre d'un bloc.
   *
   * @param {HTMLElement} root - Élément portant `data-amb-faq`.
   */
  function initFaq(root) {
    if (root.dataset.ambReady === '1') return;
    root.dataset.ambReady = '1';

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

    for (const el of root.querySelectorAll('.amb-faq__item')) {
      const item = /** @type {HTMLDetailsElement} */ (el);
      const found = item.querySelector('.amb-faq__panel');
      const summary = item.querySelector('summary');
      if (!(found instanceof HTMLElement) || !summary) continue;
      /** @type {HTMLElement} */
      const panel = found;

      /** @type {Animation|null} */
      let running = null;

      /**
       * `fill: 'both'` maintient la hauteur d'arrivée après la fin de
       * l'animation : `open` est reposé pendant que le panneau est encore tenu
       * à zéro, ce qui évite l'image intermédiaire pleine hauteur. Le remplissage
       * est relâché juste après, par `cancel()`.
       *
       * @param {number} from - Hauteur de départ, en pixels.
       * @param {number} to - Hauteur d'arrivée, en pixels.
       * @param {() => void} done - Appelé une fois l'animation terminée.
       */
      function slide(from, to, done) {
        if (running) running.cancel();
        const animation = panel.animate(
          { height: [from + 'px', to + 'px'] },
          { duration: 260, easing: 'cubic-bezier(0.2, 0.7, 0.3, 1)', fill: 'both' }
        );
        running = animation;
        animation.addEventListener('finish', function () {
          done();
          animation.cancel();
          if (running === animation) running = null;
        });
      }

      // `open` reste posé pendant toute la fermeture — c'est ce qui garde le
      // contenu rendu le temps de l'animation. Ce drapeau distingue donc « ouvert »
      // de « en train de se fermer », que l'attribut seul confond.
      let closing = false;

      summary.addEventListener('click', function (event) {
        if (reduced.matches) return;
        event.preventDefault();

        if (closing) {
          // Re-clic pendant la fermeture : on repart de la hauteur courante.
          closing = false;
          slide(panel.getBoundingClientRect().height, panel.scrollHeight, function () {});
          return;
        }

        if (item.open) {
          closing = true;
          slide(panel.getBoundingClientRect().height, 0, function () {
            item.open = false;
            closing = false;
          });
          return;
        }

        // Fermé, le panneau garde une boîte mesurable (`content-visibility`) :
        // sa hauteur courante vaut déjà la hauteur pleine, d'où le 0 en dur.
        item.open = true;
        slide(0, panel.scrollHeight, function () {});
      });
    }
  }

  /* ---------- bandeau d'annonces ---------- */
  /**
   * Fait défiler les messages du bandeau haut.
   *
   * Un seul message est visible ; l'entrant rejoue l'animation `amb-ticker-in`
   * définie dans ambinoid.css. Retirer puis remettre `is-active` ne suffit pas
   * à relancer une animation CSS : le navigateur doit voir l'élément sans la
   * classe pendant une passe de layout, d'où la lecture de `offsetWidth`.
   *
   * @param {HTMLElement} root - Élément portant `data-amb-ticker`.
   */
  function initTicker(root) {
    if (root.dataset.ambReady === '1') return;
    root.dataset.ambReady = '1';

    const items = /** @type {HTMLElement[]} */ ([...root.querySelectorAll('.amb-ticker__item')]);
    if (!items.length) return;

    const delay = parseInt(root.getAttribute('data-interval') || '', 10) || 5000;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let index = 0;
    /** @type {number|null} */
    let timer = null;

    /** @param {number} next - Index cible, non borné. */
    function show(next) {
      index = (next + items.length) % items.length;
      const active = items[index];
      if (!active) return;
      for (const el of items) el.classList.remove('is-active');
      void active.offsetWidth;
      active.classList.add('is-active');
    }

    function stop() {
      if (timer !== null) {
        window.clearInterval(timer);
        timer = null;
      }
    }

    function start() {
      if (reduce || items.length < 2) return;
      stop();
      timer = window.setInterval(() => show(index + 1), delay);
    }

    /**
     * @param {string} selector - Sélecteur du bouton.
     * @param {number} step - Décalage appliqué à l'index.
     */
    function wire(selector, step) {
      const btn = root.querySelector(selector);
      if (!btn) return;
      btn.addEventListener('click', () => {
        show(index + step);
        start();
      });
    }

    wire('[data-amb-ticker-prev]', -1);
    wire('[data-amb-ticker-next]', 1);

    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', start);

    show(0);
    start();
  }

  /* ---------- champs de recherche de l'en-tête ---------- */

  /**
   * Câblé une seule fois par page. Les écouteurs sont délégués au document
   * plutôt que posés sur les boutons : l'éditeur de thème remplace le noeud de
   * l'en-tête à chaque sauvegarde, et une référence capturée deviendrait
   * orpheline. Rien ici n'est nécessaire au rendu — les deux pilules s'ouvrent
   * et se replient en CSS (`:focus-within`), le chevron a un `href` qui mène à
   * l'accueil, et la loupe mobile est un bouton d'envoi qui, sans JS, mène à
   * /search plutôt que nulle part.
   */
  let searchWired = false;

  /**
   * Vide le champ de recherche depuis sa croix, et fait de la loupe mobile
   * tour à tour une ouverture, un repli ou un envoi selon l'état de la pilule.
   */
  function initHeaderSearch() {
    if (searchWired) return;
    searchWired = true;

    document.addEventListener('click', function (event) {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const clear = target.closest('[data-amb-search-clear]');
      if (clear) {
        const form = clear.closest('form');
        const field = form ? form.querySelector('input[name="q"]') : null;
        if (field instanceof HTMLInputElement) {
          field.value = '';
          field.focus();
        }
        return;
      }

      // Trois rôles pour la loupe mobile, selon l'état de la pilule. Repliée,
      // elle n'affiche aucune requête, donc elle n'en envoie aucune :
      //   - repliée : l'appui ouvre et porte le focus dans le champ — c'est
      //     `:focus-within` qui déplie — saisie précédente comprise ;
      //   - ouverte et vide : le même appui la referme. Une fois étendue sur la
      //     rangée, la loupe est le seul repli à portée du pouce ;
      //   - ouverte et remplie : le bouton retrouve son rôle natif et envoie.
      const toggle = target.closest('[data-amb-msearch-toggle]');
      if (toggle) {
        const pill = toggle.closest('form');
        const term = pill ? pill.querySelector('input[name="q"]') : null;
        if (term instanceof HTMLInputElement) {
          // La pilule est ouverte quand le champ a le focus, et seulement là :
          // c'est exactement ce que `:focus-within` déplie. Sur la page de
          // résultats le champ porte déjà la requête, mais repliée la pilule ne
          // la montre pas plus qu'ailleurs — la loupe ouvre donc, au lieu de
          // renvoyer la recherche que le visiteur vient de lire.
          if (document.activeElement !== term) {
            event.preventDefault();
            term.focus();
          } else if (term.value.trim() === '') {
            event.preventDefault();
            term.blur();
          }
        }
        return;
      }
    });

    // L'ouverture de la pilule mobile est en CSS (`:focus-within`), et la
    // bascule lue plus haut se joue sur `document.activeElement` : le doigt qui
    // se pose sur la loupe emporterait le focus du champ vers le bouton, et le
    // clic de dessus verrait un champ déjà relâché — le second appui rouvrirait
    // au lieu de refermer. `preventDefault` sur l'appui annule ce seul
    // déplacement de focus ; le clic, lui, part normalement, et l'état lu plus
    // haut est bien celui d'avant le geste.
    document.addEventListener('mousedown', function (event) {
      const target = event.target;
      if (target instanceof Element && target.closest('[data-amb-msearch-toggle]')) event.preventDefault();
    });
  }

  /* ---------- suggestions de la recherche de l'en-tête ---------- */

  /**
   * En deçà, Shopify ne suggère rien d'utile. Deux et pas trois : « Öl » est un
   * mot entier du catalogue.
   */
  const SUGGEST_MIN = 2;

  /** Attente après la dernière frappe, en millisecondes, avant de demander. */
  const SUGGEST_DELAY = 180;

  /** Réponses retenues, par URL : revenir d'une lettre ne redemande rien. */
  const SUGGEST_CACHE_SIZE = 24;

  let suggestWired = false;

  /**
   * La liste qui s'ouvre sous les deux pilules pendant la frappe.
   *
   * Le contenu est du Liquid : sections/ambinoid-search-suggest.liquid, rendue
   * par l'API de recherche prédictive de Shopify dans la langue de la page. Ce
   * script ne fait que la demander, la poser dans le panneau que le champ
   * désigne par `aria-controls`, et la piloter au clavier.
   *
   * Trois choses ne se voient ni dans le Liquid ni dans la feuille :
   *
   *   - le focus ne quitte jamais le champ. Les deux pilules s'ouvrent en CSS
   *     sur `:focus-within`, comme le reste de l'en-tête : un appui qui
   *     porterait le focus sur une suggestion replierait la pilule sous le
   *     doigt, et le panneau avec elle, avant que le clic n'arrive. D'où le
   *     `preventDefault` de l'appui sur le panneau — le même geste que pour la
   *     loupe mobile —, et le motif combobox de l'APG : les flèches promènent
   *     `aria-activedescendant`, pas le focus ;
   *   - une réponse arrivée tard ne s'affiche que si le champ dit encore ce
   *     qu'elle cherchait, et qu'il a toujours le focus. La réponse de « gr »
   *     peut tomber pendant qu'on tape la suite, avant que la demande suivante
   *     ne parte et ne l'annule : sans ce contrôle, elle passerait un instant
   *     sous « grin ». L'annulation n'est qu'une économie de réseau ;
   *   - Entrée sans option désignée garde son rôle natif : le formulaire part,
   *     comme avant cette liste. Avec une option désignée, c'est l'option.
   *
   * Câblé une seule fois, au document, pour la raison de `initHeaderSearch()` :
   * l'éditeur de thème remplace l'en-tête à chaque sauvegarde.
   */
  function initSearchSuggest() {
    if (suggestWired) return;
    suggestWired = true;

    /** @type {WeakMap<HTMLInputElement, AmbSuggestState>} */
    const states = new WeakMap();
    /** @type {Map<string, string>} */
    const cache = new Map();

    /**
     * @param {HTMLInputElement} input - Champ de recherche de l'en-tête.
     * @returns {AmbSuggestState}
     */
    function stateOf(input) {
      let state = states.get(input);
      if (!state) {
        state = { timer: 0, controller: null, term: '', active: -1 };
        states.set(input, state);
      }
      return state;
    }

    /**
     * @param {EventTarget|null} target - Cible d'un événement.
     * @returns {HTMLInputElement|null} Le champ, si c'en est un qui suggère.
     */
    function fieldOf(target) {
      return target instanceof HTMLInputElement && target.hasAttribute('data-amb-suggest-input') ? target : null;
    }

    /**
     * @param {HTMLInputElement} input - Champ de recherche.
     * @returns {HTMLElement|null} Le panneau que le champ désigne.
     */
    function panelOf(input) {
      const id = input.getAttribute('aria-controls');
      const panel = id ? document.getElementById(id) : null;
      return panel instanceof HTMLElement && panel.hasAttribute('data-amb-suggest') ? panel : null;
    }

    /**
     * @param {HTMLElement} panel - Panneau de suggestions.
     * @returns {HTMLAnchorElement[]}
     */
    function optionsOf(panel) {
      /** @type {HTMLAnchorElement[]} */
      const options = [];
      for (const option of panel.querySelectorAll('[data-amb-suggest-option]')) {
        if (option instanceof HTMLAnchorElement) options.push(option);
      }
      return options;
    }

    /**
     * La région `role="status"` voisine du panneau.
     *
     * @param {HTMLElement} panel - Panneau de suggestions.
     * @returns {HTMLElement|null}
     */
    function statusOf(panel) {
      const status = panel.parentElement ? panel.parentElement.querySelector('[data-amb-suggest-status]') : null;
      return status instanceof HTMLElement ? status : null;
    }

    /**
     * Plafonne la hauteur du panneau à ce qui reste visible sous lui. Sur
     * téléphone, c'est le clavier qui décide : il rétrécit la fenêtre visible
     * sans toucher à la fenêtre de mise en page, et une liste qui filerait
     * dessous cacherait justement ses derniers produits.
     *
     * @param {HTMLElement} panel - Panneau ouvert.
     */
    function fit(panel) {
      const view = window.visualViewport;
      const bottom = view ? view.offsetTop + view.height : window.innerHeight;
      const room = Math.floor(bottom - panel.getBoundingClientRect().top - 12);
      panel.style.setProperty('--amb-suggest-room', Math.max(room, 160) + 'px');
    }

    /**
     * @param {HTMLInputElement} input - Champ de recherche.
     * @param {HTMLElement} panel - Son panneau.
     */
    function open(input, panel) {
      panel.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      fit(panel);
    }

    /**
     * Referme le panneau et oublie ce qui était en route. Son contenu reste : un
     * retour dans le champ sur le même terme le rouvre tel quel.
     *
     * @param {HTMLInputElement} input - Champ de recherche.
     */
    function close(input) {
      const state = stateOf(input);
      window.clearTimeout(state.timer);
      state.timer = 0;
      if (state.controller) state.controller.abort();
      state.controller = null;

      const panel = panelOf(input);
      input.setAttribute('aria-expanded', 'false');
      setActive(input, -1);
      if (!panel) return;
      panel.hidden = true;
      const status = statusOf(panel);
      if (status) status.textContent = '';
    }

    /**
     * Désigne une option au clavier, ou rend la main au champ avec `-1`.
     *
     * @param {HTMLInputElement} input - Champ de recherche.
     * @param {number} index - Rang de l'option, `-1` pour aucune.
     */
    function setActive(input, index) {
      const state = stateOf(input);
      const panel = panelOf(input);
      const options = panel ? optionsOf(panel) : [];
      state.active = index >= 0 && index < options.length ? index : -1;

      options.forEach((option, i) => option.setAttribute('aria-selected', i === state.active ? 'true' : 'false'));
      const current = options[state.active];
      if (current) {
        input.setAttribute('aria-activedescendant', current.id);
        current.scrollIntoView({ block: 'nearest' });
      } else {
        input.removeAttribute('aria-activedescendant');
      }
    }

    /**
     * Pose une réponse dans le panneau, si elle vaut encore.
     *
     * @param {HTMLInputElement} input - Champ de recherche.
     * @param {HTMLElement} panel - Son panneau.
     * @param {string} term - Le terme que la réponse suggère.
     * @param {string} html - La section rendue par Shopify.
     */
    function show(input, panel, term, html) {
      if (document.activeElement !== input || input.value.trim() !== term) return;

      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const results = parsed.querySelector('[data-amb-suggest-results]');
      if (!results || !results.querySelector('[data-amb-suggest-option]')) {
        close(input);
        return;
      }

      const content = /** @type {HTMLElement} */ (document.importNode(results, true));
      // La même réponse peut remplir l'un ou l'autre panneau : les `id` que
      // `aria-activedescendant` vise sont donc posés ici, préfixés du panneau.
      content.querySelectorAll('[data-amb-suggest-option]').forEach((option, i) => {
        option.id = panel.id + '-' + i;
      });
      panel.replaceChildren(content);
      stateOf(input).term = term;
      setActive(input, -1);
      open(input, panel);

      const announce = content.querySelector('[data-amb-suggest-announce]');
      const status = statusOf(panel);
      if (status && announce) status.textContent = (announce.textContent || '').trim();
    }

    /**
     * @param {HTMLInputElement} input - Champ de recherche.
     * @param {HTMLElement} panel - Son panneau.
     * @param {string} term - Le terme à suggérer.
     */
    function request(input, panel, term) {
      const state = stateOf(input);
      const url = new URL(panel.dataset.ambSuggestUrl || '/search/suggest', window.location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('section_id', panel.dataset.ambSuggest || '');
      url.searchParams.set('resources[type]', 'query,product,collection');
      url.searchParams.set('resources[limit]', '4');
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      const key = url.toString();

      if (state.controller) state.controller.abort();
      state.controller = null;

      const known = cache.get(key);
      if (known !== undefined) {
        show(input, panel, term, known);
        return;
      }

      const controller = new AbortController();
      state.controller = controller;
      fetch(key, { signal: controller.signal })
        .then((response) => (response.ok ? response.text() : null))
        .then((html) => {
          if (state.controller === controller) state.controller = null;
          // Une recherche qui échoue ne casse rien : Entrée mène toujours à la
          // page de résultats. Le panneau se tait, simplement.
          if (html === null) {
            close(input);
            return;
          }
          cache.set(key, html);
          if (cache.size > SUGGEST_CACHE_SIZE) {
            const oldest = cache.keys().next().value;
            if (oldest !== undefined) cache.delete(oldest);
          }
          show(input, panel, term, html);
        })
        .catch(() => {
          if (!controller.signal.aborted) close(input);
        });
    }

    document.addEventListener('input', function (event) {
      const input = fieldOf(event.target);
      const panel = input ? panelOf(input) : null;
      if (!input || !panel) return;

      const state = stateOf(input);
      window.clearTimeout(state.timer);
      setActive(input, -1);

      const term = input.value.trim();
      if (term.length < SUGGEST_MIN) {
        close(input);
        return;
      }
      state.timer = window.setTimeout(function () {
        state.timer = 0;
        request(input, panel, term);
      }, SUGGEST_DELAY);
    });

    document.addEventListener('keydown', function (event) {
      const input = fieldOf(event.target);
      const panel = input ? panelOf(input) : null;
      if (!input || !panel) return;

      const state = stateOf(input);
      const options = optionsOf(panel);

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        if (panel.hidden) {
          // Rouvre ce qui a été refermé par Échap, sur le même terme.
          if (event.key !== 'ArrowDown' || !options.length || state.term !== input.value.trim()) return;
          event.preventDefault();
          open(input, panel);
          return;
        }
        event.preventDefault();
        // `-1` compte comme une position : la flèche repasse par le champ.
        const span = options.length + 1;
        const step = event.key === 'ArrowDown' ? 1 : -1;
        setActive(input, ((state.active + 1 + step + span) % span) - 1);
        return;
      }

      if (event.key === 'Enter' && !panel.hidden && state.active >= 0) {
        const option = options[state.active];
        if (!option) return;
        event.preventDefault();
        option.click();
        return;
      }

      if (event.key === 'Escape' && !panel.hidden) {
        // Le premier Échap referme la liste ; le suivant retrouve son rôle
        // natif sur un champ `type="search"`, qui est de le vider.
        event.preventDefault();
        close(input);
      }
    });

    document.addEventListener('focusout', function (event) {
      const input = fieldOf(event.target);
      if (input) close(input);
    });

    // Retour dans le champ sur le terme dont le panneau porte encore les
    // suggestions — l'onglet quitté puis repris, la loupe mobile rouverte : la
    // liste revient telle quelle, sans redemander.
    document.addEventListener('focusin', function (event) {
      const input = fieldOf(event.target);
      const panel = input ? panelOf(input) : null;
      if (!input || !panel || !panel.firstElementChild) return;
      if (stateOf(input).term !== input.value.trim() || input.value.trim().length < SUGGEST_MIN) return;
      open(input, panel);
    });

    // Voir l'en-tête : l'appui ne doit pas emporter le focus du champ, sans
    // quoi la pilule se replie sous le doigt et le clic tombe dans le vide.
    document.addEventListener('mousedown', function (event) {
      const target = event.target;
      if (target instanceof Element && target.closest('[data-amb-suggest]')) event.preventDefault();
    });

    // Le cache d'historique rend la page telle qu'elle est partie : panneau
    // ouvert, mais plus aucun focus pour le refermer.
    window.addEventListener('pageshow', function (event) {
      if (!event.persisted) return;
      for (const input of document.querySelectorAll('[data-amb-suggest-input]')) {
        if (input instanceof HTMLInputElement) close(input);
      }
    });

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', function () {
        const input = fieldOf(document.activeElement);
        const panel = input ? panelOf(input) : null;
        if (panel && !panel.hidden) fit(panel);
      });
    }
  }

  /* ---------- infolettre du pied de page ---------- */

  /**
   * Où l'adresse attend entre la soumission et la page d'arrivée.
   *
   * Elle ne peut pas être relue du champ au retour : Shopify redirige, la page
   * est neuve, le champ est vide. Et elle ne peut pas partir au moment de la
   * soumission non plus — voir `subscribe()`. Elle transite donc par le seul
   * endroit qui survit à une navigation dans le même onglet, sur l'appareil du
   * visiteur, et elle en est retirée dès qu'elle est reprise.
   */
  const NEWSLETTER_KEY = 'ambinoid:newsletter-pending';

  /**
   * Pose l'adresse dans la liste Klaviyo de la langue de la page.
   *
   * Chemin optionnel, éteint par défaut : la section ne rend les attributs
   * `data-amb-newsletter-*` que si elle porte une clé publique Klaviyo, et
   * elle n'en porte plus depuis le 8 septembre 2026. Le consentement est le
   * double opt-in de Shopify, et Klaviyo le reçoit par son intégration ; le
   * commentaire de la section dit le reste. Le code demeure pour le jour où
   * l'on voudrait une liste Klaviyo par langue, et les tests le tiennent.
   *
   * **Après Shopify, jamais pendant.** Le formulaire est celui du captcha
   * invisible : la page part en navigation dès que le jeton est obtenu, et une
   * requête lancée au clic court contre cette navigation. Ici il n'y a pas de
   * course — on est sur la page d'arrivée, et on n'y arrive que si Shopify a
   * pris l'adresse. Klaviyo n'entend donc parler que d'adresses que Shopify a
   * acceptées, et les deux systèmes ne peuvent pas diverger dans ce sens-là.
   *
   * Le consentement est dit explicitement (`SUBSCRIBED`), et c'est le double
   * opt-in **de la liste Klaviyo** qui le confirme par e-mail. `consented_at`
   * n'est pas envoyé, et ce n'est pas un oubli : ce champ est réservé à
   * l'import d'un consentement ancien, et l'API refuse l'appel entier si on le
   * pose sans le drapeau qui va avec. Klaviyo horodate lui-même.
   *
   * La clé est le « site ID » public de Klaviyo, celui que son app embed pose
   * déjà dans chaque page de la boutique. Elle n'autorise que cet appel-là, et
   * `credentials` reste au défaut : rien de la boutique ne part avec.
   *
   * Un échec ne se peint pas. Shopify a l'adresse, la page l'a dit, et une
   * seconde phrase sur un dépôt que le visiteur ne connaît pas ne lui
   * apprendrait rien. Il part en console, là où on le cherche.
   *
   * @param {HTMLElement} fields - Le bloc `[data-amb-newsletter-fields]`.
   * @param {string} address - L'adresse que Shopify vient d'accepter.
   */
  function subscribe(fields, address) {
    const company = fields.dataset.ambNewsletterCompany;
    const list = fields.dataset.ambNewsletterList;
    if (!company || !list || !address) return;
    if (typeof window.fetch !== 'function') return;

    /** @type {Record<string, unknown>} */
    const profile = {
      email: address,
      subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
    };
    // La langue sur le profil, en plus de la liste : une adresse qui passera un
    // jour d'une liste à l'autre garde ce qu'elle a dit en s'inscrivant.
    const locale = fields.dataset.ambNewsletterLocale;
    if (locale) profile.locale = locale;

    fetch('https://a.klaviyo.com/client/subscriptions?company_id=' + encodeURIComponent(company), {
      method: 'POST',
      headers: { 'content-type': 'application/json', revision: KLAVIYO_REVISION },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Footer newsletter',
            profile: { data: { type: 'profile', attributes: profile } },
          },
          relationships: { list: { data: { type: 'list', id: list } } },
        },
      }),
      // La confirmation est sous les yeux du visiteur : il peut cliquer
      // ailleurs dans la seconde. `keepalive` laisse la requête finir sa course
      // même si la page part.
      keepalive: true,
    })
      .then((response) => {
        if (!response.ok) console.error('[ambinoid] newsletter Klaviyo', response.status);
      })
      .catch((error) => console.error('[ambinoid] newsletter Klaviyo', error));
  }

  /**
   * Accompagne l'inscription à l'infolettre, sans s'en mêler.
   *
   * Ce fichier a longtemps posté le formulaire lui-même, en `fetch`, pour éviter
   * le rechargement. Cela ne pouvait pas marcher, et voici pourquoi.
   *
   * Shopify protège certains formulaires par un captcha invisible, et la liste
   * est dans le script `captcha-bootstrap` qu'il injecte dans chaque page :
   * `[['contact','contact'], ['blogs','new_comment'], ['comments','new_comment'],
   * ['contact','customer']]`. Le nôtre est le dernier de la liste, action
   * `/contact` et `form_type` `customer`. Ce script écoute `submit` sur le
   * document, retient la soumission, obtient un jeton, puis soumet le formulaire
   * **lui-même**. C'est de là que venait le rechargement, et aucun
   * `preventDefault` de notre côté ne pouvait l'empêcher.
   *
   * Notre `fetch`, lui, partait sans jeton. Relevé sur la boutique :
   * `POST /en/contact` sans jeton répond `400 Missing CAPTCHA token`. Donc
   * `response.ok` valait faux, donc on peignait un échec pendant que la
   * soumission de Shopify, elle, aboutissait. « Le message d'erreur alors que ça
   * a marché » : c'était ça, et c'était structurel, pas un défaut de réglage.
   *
   * On ne s'en mêle donc plus. Le chemin natif est celui qui fonctionne, et il
   * est bon : le Liquid rend la confirmation à la place du champ au retour, et
   * le `{% style %}` de la section retire le défilement animé de l'arrivée. Il
   * ne reste ici que ce que le Liquid ne peut pas faire.
   *
   * Et ce qu'il ne peut pas faire s'est agrandi d'un destinataire optionnel :
   * une liste Klaviyo par langue, quand la section porte une clé publique.
   * `subscribe()` s'en charge, et le fait **après** Shopify : voir son en-tête,
   * qui dit aussi pourquoi la clé est vide par défaut.
   *
   * @param {HTMLElement} fields - Le bloc `[data-amb-newsletter-fields]`.
   */
  function initNewsletter(fields) {
    if (fields.dataset.ambReady === '1') return;
    const form = fields.closest('form');
    if (!(form instanceof HTMLFormElement)) return;
    fields.dataset.ambReady = '1';

    const email = form.querySelector('input[type="email"]');
    const success = form.querySelector('[data-amb-newsletter-success]');
    const failure = form.querySelector('[data-amb-newsletter-error]');

    /** L'issue que le Liquid vient de rendre, s'il en a rendu une. */
    const issue = fields.dataset.ambNewsletterState === 'done' ? success : failure;

    /**
     * Efface l'état posé par le Liquid au retour de Shopify.
     *
     * Une frappe dans le champ est la réponse au refus : le message et le liseré
     * n'ont plus rien à dire, et les laisser laisse croire que la correction n'a
     * pas été prise en compte.
     */
    function clear() {
      delete fields.dataset.ambNewsletterState;
      for (const shown of [success, failure]) {
        if (shown instanceof HTMLElement) shown.hidden = true;
      }
      if (email instanceof HTMLInputElement) {
        email.removeAttribute('aria-invalid');
        email.removeAttribute('aria-describedby');
      }
    }

    if (email instanceof HTMLInputElement) {
      email.addEventListener('input', () => {
        if (fields.dataset.ambNewsletterState === 'error') clear();
      });
    }

    // Le focus va sur l'issue que le Liquid vient de rendre. `role="status"` ne
    // fait rien d'un message présent dès le chargement : c'est un contenu
    // initial, pas une mise à jour, et le lecteur d'écran ne le lit pas. Sans ce
    // déplacement, le visiteur qui vient de s'inscrire arrive sur une page qui
    // ne lui dit rien. `preventScroll` parce que le navigateur a déjà placé la
    // page sur l'ancre du formulaire.
    if (issue instanceof HTMLElement && !issue.hidden) {
      // Différé d'une trame : après une navigation par soumission de formulaire,
      // le navigateur remet le focus à `body` une fois les scripts différés
      // passés. Mesuré : appelé tout de suite, le déplacement ne tient pas, et
      // le visiteur qui vient de s'inscrire arrive sur une page muette.
      requestAnimationFrame(() => issue.focus({ preventScroll: true }));
    }

    // La seconde moitié de l'inscription. L'adresse mise de côté au départ est
    // reprise **et retirée** à chaque chargement, quelle que soit l'issue : une
    // tentative, pas de reprise. Un échec réseau ne la perd pas vraiment —
    // Shopify l'a, et son intégration Klaviyo reste le filet — tandis qu'une
    // adresse qui resterait là repartirait à l'inscription suivante, et
    // patienterait d'ici là dans le navigateur de quelqu'un qui a peut-être
    // quitté la page depuis longtemps.
    //
    // Et elle ne part chez Klaviyo que sur la page que Shopify rend quand il a
    // pris l'adresse : un refus, ou un simple passage sur une autre page, la
    // jette sans rien envoyer.
    let stashed = '';
    try {
      stashed = window.sessionStorage.getItem(NEWSLETTER_KEY) || '';
      window.sessionStorage.removeItem(NEWSLETTER_KEY);
    } catch (error) {
      // Navigation privée : Shopify garde l'adresse, Klaviyo ne l'aura que par
      // l'intégration, sans la langue.
    }
    if (fields.dataset.ambNewsletterState === 'done') subscribe(fields, stashed);

    // Entre le clic et le rechargement, il se passe deux choses invisibles : le
    // captcha de Shopify résout son défi, puis la page fait l'aller-retour. Sur
    // un réseau lent, cela fait plusieurs secondes devant un bouton qui ne dit
    // rien. On peint donc l'attente, et rien d'autre : la soumission n'est pas
    // retenue, c'est tout l'objet de ce qui précède.
    form.addEventListener('submit', () => {
      if (!form.checkValidity()) return;
      fields.dataset.ambNewsletterState = 'pending';
      fields.setAttribute('aria-busy', 'true');

      // Et on met l'adresse de côté pour la page d'arrivée, qui ne l'aura plus.
      try {
        window.sessionStorage.setItem(NEWSLETTER_KEY, email instanceof HTMLInputElement ? email.value : '');
      } catch (error) {
        // Navigation privée : l'inscription Shopify part quand même, entière.
      }
    });

    // Revenue par le bouton « précédent », la page sort du cache d'historique
    // telle qu'elle était partie, tour d'attente compris. Il tournerait sur une
    // page qui n'attend plus rien.
    window.addEventListener('pageshow', () => {
      if (fields.dataset.ambNewsletterState !== 'pending') return;
      delete fields.dataset.ambNewsletterState;
      fields.removeAttribute('aria-busy');
    });
  }

  /* ---------- en-tête épinglé : escamotage au défilement ---------- */

  /**
   * Câblé une seule fois par page, sur le modèle du mini-panier : les écouteurs
   * vivent sur le conteneur de défilement et relisent l'en-tête à chaque
   * passage. L'éditeur de thème remplace le noeud à chaque sauvegarde de la
   * section, une référence capturée deviendrait orpheline.
   */
  let headerWired = false;

  /**
   * Escamote l'en-tête vers le haut au défilement descendant et le ramène au
   * défilement montant.
   *
   * L'épinglage lui-même est en CSS (`position: sticky`) : cette fonction ne
   * pose que l'état, donc un en-tête privé de JS reste visible et utilisable.
   */
  function initStickyHeader() {
    if (headerWired) return;
    if (!document.querySelector('[data-amb-sticky]')) return;
    headerWired = true;

    // Au-dessus de 990px Horizon confie le défilement à `.page-wrapper`
    // (`html` et `body` passent en `overflow: hidden`) ; en dessous c'est la
    // fenêtre. Les deux sont écoutés, l'un des deux est toujours à zéro.
    const scrollEl = /** @type {HTMLElement|null} */ (document.querySelector('.page-wrapper'));

    /** En deçà, le mouvement est tenu pour du bruit : tremblement, rebond iOS. */
    const THRESHOLD = 8;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /** @returns {HTMLElement|null} */
    function headerEl() {
      return /** @type {HTMLElement|null} */ (document.querySelector('[data-amb-sticky]'));
    }

    /** @returns {number} Position de défilement, quel que soit le conteneur qui défile. */
    function scrollTop() {
      const fromWrapper = scrollEl ? scrollEl.scrollTop : 0;
      return fromWrapper || window.scrollY;
    }

    let last = scrollTop();

    function update() {
      const header = headerEl();
      if (!header) return;

      // `getBoundingClientRect` intègre la `transform` de l'élément mesuré : on
      // interroge le conteneur de section, lui jamais translaté. Mesurer
      // l'en-tête ferait dépendre la condition de sortie de l'état qu'elle doit
      // annuler, et l'en-tête resterait escamoté au retour en haut de page.
      const shell = header.closest('.amb-header-section');
      const pinned = shell !== null && shell.getBoundingClientRect().top <= 0;
      header.classList.toggle('is-stuck', pinned);
      if (reduce) return;

      const top = scrollTop();
      const delta = top - last;

      if (!pinned || top <= header.offsetHeight) {
        // Encore à sa place naturelle, ou à peine au-delà : rien à escamoter.
        header.classList.remove('is-hidden');
      } else if (delta > THRESHOLD) {
        header.classList.add('is-hidden');
      } else if (delta < -THRESHOLD) {
        header.classList.remove('is-hidden');
      }

      // Le repère ne suit que les mouvements retenus : un défilement lent
      // s'accumule ainsi jusqu'au seuil, au lieu d'être rogné image par image
      // et de ne jamais l'atteindre.
      if (delta > THRESHOLD || delta < -THRESHOLD) last = top;
    }

    let ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        update();
        ticking = false;
      });
    }

    if (scrollEl) scrollEl.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });

    // Escamoté, l'en-tête garde sa place dans l'ordre de tabulation : sans ce
    // rappel, la navigation au clavier piloterait une barre hors écran.
    document.addEventListener('focusin', function (event) {
      const header = headerEl();
      if (!header || !(event.target instanceof Node)) return;
      if (header.contains(event.target)) header.classList.remove('is-hidden');
    });

    update();
  }

  /* ---------- carte du magasin : déplacement et zoom ---------- */

  /** Hauteur de texte visée pour les noms de rue, en pixels écran. */
  const MAP_LABEL_PX = 8;

  /** Bandes de détail, de la plus serrée à la plus large (largeur de vue en mètres). */
  const MAP_BANDS = [
    { max: 400, name: 'near' },
    { max: 950, name: 'mid' },
    { max: Infinity, name: 'far' },
  ];

  /**
   * Lit un rectangle sérialisé en attribut, au format `"x y largeur hauteur"`.
   *
   * @param {Element} el - Élément porteur.
   * @param {string} name - Nom de l'attribut.
   * @returns {{ x: number, y: number, w: number, h: number } | null} Null si absent ou illisible.
   */
  function readMapRect(el, name) {
    const parts = (el.getAttribute(name) || '').trim().split(/\s+/).map(Number);
    const [x, y, w, h] = parts;
    if (x === undefined || y === undefined || w === undefined || h === undefined) return null;
    if (!Number.isFinite(x) || !Number.isFinite(y) || w <= 0 || h <= 0) return null;
    return { x, y, w, h };
  }

  /**
   * Ramène une valeur dans un intervalle. Quand l'intervalle est vide — la vue
   * est plus large que les données — on renvoie son milieu, ce qui centre.
   *
   * @param {number} value
   * @param {number} lo
   * @param {number} hi
   * @returns {number}
   */
  function clampMap(value, lo, hi) {
    if (hi < lo) return (lo + hi) / 2;
    return Math.min(hi, Math.max(lo, value));
  }

  /**
   * Câble la carte injectée : bornes, déplacement, zoom, contrôles, clavier.
   *
   * Le SVG porte ses propres limites (`data-extent`, `data-home`,
   * `data-view-range`), en mètres : le JS ne connaît aucune coordonnée en dur.
   *
   * @param {HTMLElement} root - Conteneur `.amb-footer__map`.
   * @param {SVGSVGElement} svg - Carte injectée dans ce conteneur.
   */
  function wireMap(root, svg) {
    const bounds = readMapRect(svg, 'data-extent');
    const start = readMapRect(svg, 'data-home');
    if (!bounds || !start) return;
    const extent = bounds;
    const home = start;

    const range = (svg.getAttribute('data-view-range') || '').trim().split(/\s+/).map(Number);
    const [rawMin, rawMax] = range;
    const spanMin = rawMin !== undefined && rawMin > 0 ? rawMin : home.w / 3;
    const spanMax = rawMax !== undefined && rawMax > 0 ? rawMax : extent.w;

    const view = { cx: home.x + home.w / 2, cy: home.y + home.h / 2, w: home.w };

    /**
     * Borne l'état courant puis l'écrit dans le SVG.
     *
     * @param {{ x: number, y: number } | null} [anchor] - Point écran à garder fixe pendant le zoom.
     * @param {number} [nextWidth] - Largeur de vue demandée, en mètres.
     */
    function apply(anchor, nextWidth) {
      const box = svg.getBoundingClientRect();
      if (box.width <= 0 || box.height <= 0) return;

      const ratio = box.height / box.width;
      const maxWidth = Math.min(spanMax, extent.w, extent.h / ratio);
      const width = clampMap(nextWidth === undefined ? view.w : nextWidth, Math.min(spanMin, maxWidth), maxWidth);

      if (anchor) {
        // Le point du monde sous le curseur doit y rester : on recale le centre
        // en fonction de l'échelle d'avant et de celle d'après.
        const dx = anchor.x - box.left - box.width / 2;
        const dy = anchor.y - box.top - box.height / 2;
        const before = box.width / view.w;
        const after = box.width / width;
        view.cx = view.cx + dx / before - dx / after;
        view.cy = view.cy + dy / before - dy / after;
      }

      view.w = width;
      const height = width * ratio;
      view.cx = clampMap(view.cx, extent.x + width / 2, extent.x + extent.w - width / 2);
      view.cy = clampMap(view.cy, extent.y + height / 2, extent.y + extent.h - height / 2);

      svg.setAttribute(
        'viewBox',
        [view.cx - width / 2, view.cy - height / 2, width, height].map((n) => n.toFixed(2)).join(' ')
      );

      // Un mètre vaut `unit` unités du dessin : tout ce qui doit garder sa
      // taille à l'écran (repères, noms de rue) est contre-échelonné par là.
      const unit = width / box.width;
      for (const mark of svg.querySelectorAll('[data-amb-map-at]')) {
        const at = mark.getAttribute('data-amb-map-at') || '0 0';
        mark.setAttribute('transform', 'translate(' + at + ') scale(' + unit.toFixed(4) + ')');
      }
      for (const labels of svg.querySelectorAll('[data-amb-map-lab]')) {
        labels.setAttribute('font-size', (MAP_LABEL_PX * unit).toFixed(3));
      }

      const band = MAP_BANDS.find((entry) => width <= entry.max);
      svg.setAttribute('data-zoom', band ? band.name : 'far');
    }

    function recentre() {
      view.cx = home.x + home.w / 2;
      view.cy = home.y + home.h / 2;
      apply(null, home.w);
    }

    /* --- contrôles --- */

    const bar = document.createElement('div');
    bar.className = 'amb-footer__map-zoom';

    /**
     * @param {string} label - Texte lu par les lecteurs d'écran.
     * @param {string} glyph - Signe affiché.
     * @param {() => void} action - Ce que fait le bouton.
     */
    function addButton(label, glyph, action) {
      const button = document.createElement('button');
      button.type = 'button';
      button.setAttribute('aria-label', label);
      button.textContent = glyph;
      button.addEventListener('click', action);
      bar.appendChild(button);
    }

    const strings = root.dataset;
    addButton(strings.mapIn || 'Zoom in', '+', () => apply(null, view.w / 1.6));
    addButton(strings.mapOut || 'Zoom out', '−', () => apply(null, view.w * 1.6));
    addButton(strings.mapHome || 'Recentre', '⌖', recentre);
    root.appendChild(bar);

    const hint = document.createElement('p');
    hint.className = 'amb-footer__map-hint';
    hint.hidden = true;
    // `metaKey` sur Mac, `ctrlKey` ailleurs : la chaîne suit la plateforme.
    const mac = /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
    hint.textContent = (mac ? strings.mapHintMac : strings.mapHint) || 'Hold ctrl and scroll to zoom';
    root.appendChild(hint);

    let hintTimer = 0;
    function flashHint() {
      hint.hidden = false;
      window.clearTimeout(hintTimer);
      hintTimer = window.setTimeout(() => {
        hint.hidden = true;
      }, 1700);
    }

    /* --- déplacement --- */

    /** Pointeurs actifs, pour distinguer un glissement d'un pincement. @type {Map<number, { x: number, y: number }>} */
    const pointers = new Map();
    /** Écart entre les deux doigts au dernier événement, 0 hors pincement. */
    let pinch = 0;

    svg.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (pointers.size === 1) {
        svg.setPointerCapture(event.pointerId);
        root.classList.add('is-panning');
      }
    });

    svg.addEventListener('pointermove', (event) => {
      const previous = pointers.get(event.pointerId);
      if (!previous) return;
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (pointers.size >= 2) {
        const [a, b] = Array.from(pointers.values());
        if (!a || !b) return;
        const spread = Math.hypot(a.x - b.x, a.y - b.y);
        if (pinch > 0 && spread > 0) {
          apply({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }, (view.w * pinch) / spread);
        }
        pinch = spread;
        return;
      }

      const box = svg.getBoundingClientRect();
      if (box.width <= 0) return;
      const metresPerPixel = view.w / box.width;
      view.cx -= (event.clientX - previous.x) * metresPerPixel;
      view.cy -= (event.clientY - previous.y) * metresPerPixel;
      apply();
    });

    /** @param {PointerEvent} event */
    function releasePointer(event) {
      pointers.delete(event.pointerId);
      if (pointers.size < 2) pinch = 0;
      if (pointers.size === 0) root.classList.remove('is-panning');
    }

    svg.addEventListener('pointerup', releasePointer);
    svg.addEventListener('pointercancel', releasePointer);

    /* --- zoom --- */

    svg.addEventListener(
      'wheel',
      (event) => {
        // Sans touche de modification la molette appartient à la page : un
        // footer qui capture le défilement piège le visiteur.
        if (!event.ctrlKey && !event.metaKey) {
          flashHint();
          return;
        }
        event.preventDefault();
        apply({ x: event.clientX, y: event.clientY }, view.w * Math.exp(event.deltaY * 0.0016));
      },
      { passive: false }
    );

    svg.addEventListener('dblclick', (event) => {
      event.preventDefault();
      apply({ x: event.clientX, y: event.clientY }, view.w / 1.8);
    });

    root.addEventListener('keydown', (event) => {
      // Un lien ou un bouton posé sur la carte garde ses propres touches.
      if (event.target instanceof Element && event.target.closest('a, button')) return;
      const step = view.w * 0.18;
      switch (event.key) {
        case 'ArrowLeft':
          view.cx -= step;
          break;
        case 'ArrowRight':
          view.cx += step;
          break;
        case 'ArrowUp':
          view.cy -= step;
          break;
        case 'ArrowDown':
          view.cy += step;
          break;
        case '+':
        case '=':
          apply(null, view.w / 1.6);
          event.preventDefault();
          return;
        case '-':
        case '_':
          apply(null, view.w * 1.6);
          event.preventDefault();
          return;
        case '0':
        case 'Home':
          recentre();
          event.preventDefault();
          return;
        default:
          return;
      }
      event.preventDefault();
      apply();
    });

    // Le bloc change de largeur entre desktop et mobile : le rapport du
    // viewBox doit suivre, sinon la carte s'étire.
    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(() => apply()).observe(root);
    } else {
      window.addEventListener('resize', () => apply());
    }

    apply();
  }

  /**
   * Remplace la carte statique du footer par un widget déplaçable et zoomable.
   *
   * Amélioration progressive : le HTML sert un `<img>`, seule chose affichée si
   * le JS ne tourne pas. Le SVG est ensuite relu depuis la même URL — donc
   * depuis le cache du navigateur, sans second téléchargement — puis injecté
   * dans le document pour devenir pilotable.
   *
   * @param {HTMLElement} root - Élément portant `data-amb-map`.
   */
  function initMap(root) {
    if (root.dataset.ambReady === '1') return;
    const rawSrc = root.getAttribute('data-amb-map');
    const found = root.querySelector('img');
    if (!rawSrc || !(found instanceof HTMLImageElement) || typeof window.fetch !== 'function') return;
    root.dataset.ambReady = '1';
    const src = rawSrc;
    const img = found;

    let started = false;
    function load() {
      if (started) return;
      started = true;
      window
        .fetch(src)
        .then((response) => (response.ok ? response.text() : Promise.reject(new Error('http'))))
        .then((text) => {
          const parsed = new DOMParser().parseFromString(text, 'image/svg+xml');
          if (parsed.querySelector('parsererror')) return;
          const node = document.importNode(parsed.documentElement, true);
          if (!(node instanceof SVGSVGElement)) return;
          // La taille vient désormais du conteneur, plus des attributs du fichier.
          node.removeAttribute('width');
          node.removeAttribute('height');
          // Le conteneur porte le libellé accessible : le tracé devient décoratif.
          node.setAttribute('aria-hidden', 'true');
          node.removeAttribute('role');
          node.removeAttribute('aria-label');
          root.replaceChild(node, img);
          root.classList.add('amb-footer__map--live');
          root.tabIndex = 0;
          const label = root.dataset.mapAria;
          if (label) root.setAttribute('aria-label', label);
          wireMap(root, node);
        })
        .catch(() => {
          // Requête ou fichier illisible : l'image statique reste en place.
        });
    }

    // On ne télécharge qu'à l'approche du footer, comme le fait `loading="lazy"`
    // sur l'image : la requête tombe alors dans le cache du navigateur.
    if (typeof IntersectionObserver === 'function') {
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          load();
        },
        { rootMargin: '400px' }
      );
      observer.observe(root);
    } else {
      load();
    }
  }

  /**
   * Ce que le consentement de la boutique dit de la carte.
   *
   * Deux formes circulent, et il faut les deux : `currentVisitorConsent()`
   * répond `'yes'` / `'no'` / `''`, tandis que l'événement
   * `visitorConsentCollected` porte des booléens sous d'autres noms. `null`
   * dit « rien de collecté » — ce qui n'est pas un refus : la carte reste
   * alors offerte au bouton.
   *
   * La catégorie lue est `marketing`, la plus stricte des quatre pour ce
   * qu'est une carte Google embarquée : un tiers qui pose ses propres cookies.
   * Un visiteur qui n'accepte que les statistiques ne la reçoit donc pas
   * d'office, et garde le bouton pour la demander.
   *
   * @param {*} consent - Objet de consentement, quelle que soit sa forme.
   * @returns {boolean|null} Accordé, refusé, ou rien de collecté.
   */
  function readMapConsent(consent) {
    if (!consent || typeof consent !== 'object') return null;
    const value = 'marketingAllowed' in consent ? consent.marketingAllowed : consent.marketing;
    if (value === true || value === 'yes') return true;
    if (value === false || value === 'no') return false;
    return null;
  }

  /**
   * Tient la carte Google derrière le consentement du visiteur.
   *
   * Ce que le Liquid rend ne contacte personne : le tracé auto-hébergé, une
   * invite, et l'adresse de Google posée en attribut. L'iframe n'est
   * fabriquée qu'ici, et seulement quand l'un des deux consentements est
   * acquis :
   *
   *   - celui du bandeau de cookies de la boutique, lu dans l'API Customer
   *     Privacy que Shopify charge avec lui, puis suivi par l'événement
   *     `visitorConsentCollected` — qui accepte n'a donc pas à recharger ;
   *   - celui du bouton posé sur la carte, qui ne vaut que pour elle. C'est
   *     la seule voie quand la boutique ne pose aucun bandeau, et c'est ce qui
   *     rend le thème correct sans dépendre d'un réglage d'admin.
   *
   * Le dernier geste l'emporte : un refus collecté ensuite décharge l'iframe
   * et efface le bouton retenu, un clic donné après un refus vaut pour cette
   * carte-là. Rien de tout cela ne se relit dans le Liquid — d'où
   * `tests/map-consent.test.mjs`.
   *
   * @param {HTMLElement} root - Élément portant `data-amb-map-consent`.
   */
  function initMapConsent(root) {
    if (root.dataset.ambReady === '1') return;
    const rawSrc = root.getAttribute('data-amb-map-consent');
    if (!rawSrc) return;
    root.dataset.ambReady = '1';
    // Recopie : l'attribut est `string | null`, `show()` veut une chaîne.
    const src = rawSrc;

    const KEY = 'ambinoid:map-consent';

    /** @returns {boolean} Vrai si le visiteur a déjà demandé la carte. */
    function remembered() {
      try {
        return window.localStorage.getItem(KEY) === '1';
      } catch (error) {
        // Navigation privée : le choix ne vaudra que pour cette page.
        return false;
      }
    }

    /** @param {boolean} allowed - Ce que le visiteur vient de décider. */
    function remember(allowed) {
      try {
        if (allowed) window.localStorage.setItem(KEY, '1');
        else window.localStorage.removeItem(KEY);
      } catch (error) {
        // Rien à retenir : la carte se redemandera à la page suivante.
      }
    }

    /** Fabrique l'iframe, et avec elle la première requête vers Google. */
    function show() {
      if (root.querySelector('iframe')) return;
      const frame = document.createElement('iframe');
      frame.src = src;
      frame.title = root.dataset.mapEmbedTitle || '';
      frame.width = '420';
      frame.height = '190';
      frame.setAttribute('loading', 'lazy');
      frame.setAttribute('referrerpolicy', 'no-referrer');
      frame.setAttribute('allowfullscreen', '');
      // En tête du bloc : l'attribution, l'invite et le lien sont posés en
      // absolu par-dessus la boîte et doivent le rester.
      root.insertBefore(frame, root.firstChild);
      // Le tracé et l'invite s'effacent en `display: none` plutôt que d'être
      // retirés du document : l'image n'est alors jamais téléchargée, et tout
      // revient tel quel si le consentement est retiré.
      root.classList.add('amb-footer__map--consented');
    }

    /** Retire l'iframe et rend la boîte au tracé auto-hébergé. */
    function hide() {
      const frame = root.querySelector('iframe');
      if (frame) frame.remove();
      root.classList.remove('amb-footer__map--consented');
    }

    root.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element) || !target.closest('[data-amb-map-allow]')) return;
      remember(true);
      show();
    });

    document.addEventListener('visitorConsentCollected', (event) => {
      // L'écouteur vit sur le document, la carte non : dans l'éditeur de thème,
      // un re-rendu de la section laisse derrière lui un écouteur qui vise un
      // noeud détaché. Il n'a plus rien à décider.
      if (!root.isConnected) return;
      const allowed = readMapConsent(/** @type {CustomEvent} */ (event).detail);
      if (allowed === true) {
        show();
        return;
      }
      if (allowed === false) {
        // Le refus porte sur toute la boutique et vient d'être exprimé : il
        // efface le bouton retenu et décharge ce qui était déjà en place.
        remember(false);
        hide();
      }
    });

    if (remembered()) {
      show();
      return;
    }

    // L'API de consentement n'est plus chargée par le bandeau de Shopify, qui
    // est éteint et remplacé par le nôtre, mais par `withCustomerPrivacy()`,
    // qui la demande à `Shopify.loadFeatures`. Elle arrive donc après ce tour
    // de boucle, là où ce bloc la lisait jusqu'ici de façon synchrone. Lue
    // trop tôt, elle était absente et la carte restait derrière son bouton
    // pour quelqu'un qui avait pourtant déjà consenti.
    withCustomerPrivacy().then((privacy) => {
      if (!root.isConnected || !privacy) return;
      if (typeof privacy.currentVisitorConsent !== 'function') return;
      if (readMapConsent(privacy.currentVisitorConsent()) === true) show();
    });
  }
  /**
   * Le dossier local de la réponse donnée à la bannière de consentement du
   * thème. `initCookieBanner()` l'écrit et explique pourquoi il existe ; il est
   * déclaré ici parce qu'il a deux lecteurs : la bannière, et
   * `initRecent()`, qui ne se souvient des produits vus qu'avec l'accord
   * « Präferenzen ».
   */
  const CONSENT_STORE_KEY = 'ambinoid:cookie-consent';
  const CONSENT_STORE_MAX_AGE = 365 * 24 * 60 * 60 * 1000;

  /** @returns {AmbConsentDecision|null} La réponse retenue, si elle vaut encore. */
  function storedConsent() {
    try {
      const raw = window.localStorage.getItem(CONSENT_STORE_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (!data || typeof data.ts !== 'number' || !data.c) return null;
      if (Date.now() - data.ts >= CONSENT_STORE_MAX_AGE) return null;
      return {
        analytics: data.c.analytics === true,
        marketing: data.c.marketing === true,
        preferences: data.c.preferences === true,
      };
    } catch (error) {
      // Navigation privée, ou stockage plein : la question reviendra, ce qui
      // reste préférable à la tenir pour répondue.
      return null;
    }
  }

  /**
   * L'API Customer Privacy de Shopify, demandée une seule fois pour la page.
   *
   * Elle arrivait jusqu'ici en cadeau : le bandeau de cookies de Shopify la
   * posait sur `window.Shopify` en se chargeant. Ce bandeau est éteint et le
   * nôtre l'a remplacé, donc plus personne ne la charge, et tout ce qui en
   * dépend doit passer par ici. C'est `Shopify.loadFeatures` qui s'en occupe.
   *
   * La promesse est mémorisée, et pas seulement son résultat : `init()` est
   * rejoué à chaque `shopify:section:load` dans l'éditeur de thème, et une
   * demande par rejeu ferait autant de chargements.
   *
   * @type {Promise<AmbCustomerPrivacy|null>|null}
   */
  let privacyRequest = null;

  /**
   * @returns {Promise<AmbCustomerPrivacy|null>} L'API, ou `null` si la boutique
   *   ne sait pas la servir. Dans ce cas rien ne doit être supposé du
   *   consentement : ni qu'il est donné, ni qu'il est refusé.
   */
  function withCustomerPrivacy() {
    if (privacyRequest) return privacyRequest;
    privacyRequest = new Promise((resolve) => {
      const shopify = window.Shopify;
      if (shopify && shopify.customerPrivacy) {
        resolve(shopify.customerPrivacy);
        return;
      }
      if (!shopify || typeof shopify.loadFeatures !== 'function') {
        resolve(null);
        return;
      }
      shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        if (error) {
          resolve(null);
          return;
        }
        const loaded = window.Shopify;
        resolve((loaded && loaded.customerPrivacy) || null);
      });
    });
    return privacyRequest;
  }

  /**
   * Diffère un geste jusqu'à ce qu'un élément soit refermé.
   *
   * Le `hidden` est ce qu'on observe, comme `dockToAgeGate()` le fait dans
   * l'autre sens : c'est l'attribut que l'age gate se pose à lui-même, et il
   * évite d'aller câbler un signal dans une section qu'on préfère laisser
   * intacte.
   *
   * Sans élément, ou sans `MutationObserver` pour voir la sortie, le geste
   * part tout de suite : mieux vaut une bannière qui se pose trop tôt qu'une
   * bannière qui ne se pose jamais.
   *
   * @param {Element|null} el - L'élément à attendre, s'il existe.
   * @param {() => void} run - Ce qu'on fait une fois qu'il est refermé.
   */
  function whenClosed(el, run) {
    if (!(el instanceof HTMLElement) || el.hidden || typeof MutationObserver !== 'function') {
      run();
      return;
    }
    const observer = new MutationObserver(() => {
      if (!el.hidden) return;
      observer.disconnect();
      run();
    });
    observer.observe(el, { attributes: true, attributeFilter: ['hidden'] });
  }

  /**
   * Bannière de consentement.
   *
   * Elle remplace celle de Shopify, qui doit être éteinte côté boutique. Le
   * pourquoi est dans l'en-tête de `snippets/ambinoid-cookie-banner.liquid` ;
   * en un mot, celle de Shopify se pose en `z-index: 2000000` sous un age gate
   * qui est en 2147483000, donc personne ne peut l'atteindre, donc personne ne
   * consent, donc la boutique n'enregistre aucune session.
   *
   * Ce que ce code tient, et que rien d'autre ne tient :
   *
   * - l'ordre. La carte attend que l'age gate soit refermé. C'est tout le
   *   sujet, et ça ne se relit ni dans le Liquid ni dans la feuille ;
   * - le fait que la décision parte vraiment chez Shopify. `setTrackingConsent`
   *   est le seul geste qui débloque la mesure ; une bannière qui se referme
   *   sans l'appeler a exactement l'air d'une bannière qui marche ;
   * - le fait qu'accepter et refuser soient deux chemins également courts. Le
   *   balisage les rend frères, ce code ne les sépare pas : un clic chacun,
   *   même sortie.
   *
   * `sale_of_data` n'est jamais posé : la doc Shopify réserve ce signal à un
   * parcours d'opt-out engagé par le visiteur, et le GPC est honoré tout seul
   * dans les régions concernées.
   *
   * Éprouvée par tests/cookie-banner.test.mjs.
   */
  function initCookieBanner() {
    const found = document.querySelector('[data-amb-cookie]');
    if (!(found instanceof HTMLElement) || found.dataset.ambReady === '1') return;
    const banner = found;
    banner.dataset.ambReady = '1';

    const card = banner.querySelector('.amb-cookie__card');
    const intro = banner.querySelector('[data-amb-cookie-intro]');
    const prefs = banner.querySelector('[data-amb-cookie-prefs]');

    /** @type {AmbCustomerPrivacy|null} */
    let api = null;

    /**
     * Le dossier local de la réponse, et pourquoi il ne peut pas ne pas exister.
     *
     * `setTrackingConsent()` ne pose rien qui survive à la page. Sur une
     * vitrine hébergée — la nôtre —, il POSTe la décision à
     * `/api/unstable/graphql.json`, range la réponse dans
     * `window.Shopify.customerPrivacy.cachedConsent`, et s'arrête là : le
     * cookie `_tracking_consent`, il ne l'écrit que pour une vitrine headless
     * (`P()` est gardé par `headlessStorefront` dans son paquet). Le report
     * d'une page à l'autre est donc entièrement à la charge du serveur, qui
     * réémet le consentement au chargement suivant.
     *
     * Or `shouldShowBanner()` n'est rien d'autre que le drapeau
     * `display_banner` de ce même bloc. Si le POST échoue — réseau, visiteur
     * qui suit un lien dans la seconde qui suit, erreur de l'API —, plus
     * personne ne sait que quelqu'un a répondu : ni Shopify, ni le thème, qui
     * refermait la carte sans rien retenir. La question repart à chaque page,
     * et c'est exactement ce qu'on a vu en boutique.
     *
     * D'où ce dossier : il retient la réponse tout de suite, sur le poste du
     * visiteur, et il sert deux fois — à ne plus poser la question, et à
     * représenter la décision à Shopify tant qu'il la redemande. Il ne cache
     * donc pas un échec, il le rattrape : c'était une décision perdue, c'est
     * un appel rejoué. Un an, comme le cookie de Shopify.
     *
     * La clé et sa lecture vivent hors de cette fonction, plus haut
     * (`storedConsent()`) : les produits récemment consultés lisent la même
     * réponse pour savoir s'ils ont le droit de se souvenir.
     */
    const STORE_KEY = CONSENT_STORE_KEY;
    const stored = storedConsent;

    /** @param {AmbConsentDecision} consent - Ce que le visiteur vient de choisir. */
    function remember(consent) {
      try {
        window.localStorage.setItem(STORE_KEY, JSON.stringify({ ts: Date.now(), c: consent }));
      } catch (error) {
        // Voir `stored()` : rien à retenir, la carte se redemandera.
      }
    }

    /**
     * Transmet la décision à Shopify, qui reste seul à l'appliquer.
     *
     * Le rappel n'est pas décoratif : c'est par lui que `setTrackingConsent()`
     * annonce son échec, en premier argument. Le thème lui passait `() => {}`,
     * donc une décision perdue avait exactement l'air d'une décision prise.
     *
     * @param {AmbConsentDecision} consent - La décision à faire enregistrer.
     */
    function send(consent) {
      if (!api || typeof api.setTrackingConsent !== 'function') return;
      api.setTrackingConsent(consent, (error) => {
        // Rien à reprendre ici : le dossier local est déjà posé, et la
        // prochaine page rejouera l'appel tant que Shopify redemandera.
        if (error) console.warn('[ambinoid] consentement non enregistré :', error);
      });
    }

    /**
     * Publie la hauteur occupée par la carte sur la racine.
     *
     * La suggestion de langue se pose au même coin de l'écran, et les deux
     * peuvent être à l'écran en même temps : un anglophone qui arrive pour la
     * première fois les reçoit toutes les deux. Sans cette mesure, elles se
     * recouvrent. Une variable posée sur `<html>` depuis le script qui sait,
     * et non un `:has()` qui le déduirait du DOM : règle 7 de CLAUDE.md.
     */
    function measure() {
      const open = !banner.hidden && card instanceof HTMLElement;
      const height = open ? card.offsetHeight + 10 : 0;
      document.documentElement.style.setProperty('--amb-cookie-h', height + 'px');
    }

    /** @param {boolean} detail - Vrai pour montrer le détail par finalité. */
    function setPanel(detail) {
      if (intro instanceof HTMLElement) intro.hidden = detail;
      if (prefs instanceof HTMLElement) prefs.hidden = !detail;
      measure();
    }

    /** @param {boolean} detail - Sur quel écran s'ouvrir. */
    function open(detail) {
      setPanel(detail);
      banner.hidden = false;
      measure();
    }

    function close() {
      banner.hidden = true;
      measure();
    }

    /**
     * Retient la décision, la transmet à Shopify, puis referme.
     *
     * L'ordre compte, et le premier des trois est celui qui manquait :
     * l'appel à Shopify part et peut échouer, la carte se referme dans tous
     * les cas, donc si rien n'est retenu d'abord, il reste un visiteur qui a
     * répondu et deux systèmes qui l'ignorent.
     *
     * @param {AmbConsentDecision} consent - Ce que le visiteur vient de choisir.
     */
    function decide(consent) {
      remember(consent);
      send(consent);
      close();
    }

    /** @returns {AmbConsentDecision} L'état des cases, finalité par finalité. */
    function fromBoxes() {
      /** @type {AmbConsentDecision} */
      const consent = { analytics: false, marketing: false, preferences: false };
      for (const box of banner.querySelectorAll('[data-amb-cookie-input]')) {
        if (!(box instanceof HTMLInputElement)) continue;
        const key = box.dataset.ambCookieInput;
        if (key === 'analytics') consent.analytics = box.checked;
        else if (key === 'marketing') consent.marketing = box.checked;
        else if (key === 'preferences') consent.preferences = box.checked;
      }
      return consent;
    }

    /**
     * Remet les cases à l'état réellement enregistré.
     *
     * Sert à la réouverture depuis le pied de page : quelqu'un qui revient sur
     * son choix doit voir celui qu'il a fait, pas des cases vierges qui lui
     * feraient croire qu'il n'avait rien accordé.
     *
     * Shopify fait foi quand il a la réponse. Quand il ne l'a pas — l'appel
     * n'est pas passé, et c'est le cas que tout ce fichier traite —, le
     * dossier local est le seul témoin de ce que le visiteur a choisi : sans
     * ce repli, revenir sur son choix le montrerait vierge à quelqu'un qui
     * avait pourtant tout accordé.
     */
    function syncBoxes() {
      const current = api && typeof api.currentVisitorConsent === 'function' ? api.currentVisitorConsent() : null;
      const local = stored();
      for (const box of banner.querySelectorAll('[data-amb-cookie-input]')) {
        if (!(box instanceof HTMLInputElement)) continue;
        const key = box.dataset.ambCookieInput;
        if (key !== 'analytics' && key !== 'marketing' && key !== 'preferences') continue;
        const said = current ? current[key] : '';
        box.checked = said === 'yes' || said === 'no' ? said === 'yes' : local ? local[key] === true : false;
      }
    }

    banner.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('[data-amb-cookie-accept]')) {
        decide({ analytics: true, marketing: true, preferences: true });
        return;
      }
      if (target.closest('[data-amb-cookie-reject]')) {
        decide({ analytics: false, marketing: false, preferences: false });
        return;
      }
      if (target.closest('[data-amb-cookie-save]')) {
        decide(fromBoxes());
        return;
      }
      if (target.closest('[data-amb-cookie-settings]')) setPanel(true);
    });

    // Le retrait doit coûter le même geste que l'octroi : un lien du pied de
    // page rouvre le détail. L'écouteur vit sur le document parce que le pied
    // de page est une section, que l'éditeur de thème re-rend sans prévenir.
    document.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element) || !target.closest('[data-amb-cookie-open]')) return;
      event.preventDefault();
      if (!banner.isConnected) return;
      syncBoxes();
      open(true);
      // Ouverture demandée : le focus suit, sinon la carte s'ouvre hors de
      // portée du clavier, en bas d'une page qu'il faudrait reparcourir.
      const first = banner.querySelector('[data-amb-cookie-input]');
      if (first instanceof HTMLElement) first.focus();
    });

    if (typeof ResizeObserver === 'function' && card instanceof HTMLElement) {
      new ResizeObserver(measure).observe(card);
    }

    withCustomerPrivacy().then((privacy) => {
      if (!banner.isConnected || !privacy) return;
      api = privacy;
      if (typeof privacy.shouldShowBanner !== 'function') return;
      // `shouldShowBanner()` porte les deux questions d'un coup : le visiteur
      // est-il dans une région où la boutique demande le consentement, et
      // a-t-il déjà répondu. C'est le drapeau `display_banner` du bloc de
      // consentement, que le serveur réémet à chaque page.
      if (!privacy.shouldShowBanner()) return;

      // Shopify redemande. Reste à savoir laquelle des deux questions il pose :
      // personne n'a répondu, ou la réponse n'est jamais arrivée jusqu'à lui.
      // Le dossier local départage, et c'est la seule chose qui le puisse.
      const local = stored();
      if (local) {
        // Quelqu'un a répondu et Shopify l'ignore : on lui représente la
        // décision plutôt que de la redemander au visiteur. Les deux systèmes
        // convergent au lieu de diverger, et la carte ne reparaît plus.
        send(local);
        return;
      }

      whenClosed(document.getElementById('AmbAgeGate'), () => {
        if (banner.isConnected) open(false);
      });
    });
  }

  /**
   * Pose la bannière dans la carte de l'age gate, le temps qu'il reste levé.
   *
   * Le gate est modal, se peint avant tout le reste et retient le focus : une
   * bannière laissée au bas de la page serait derrière lui, hors de sa boucle
   * de tabulation, et masquée aux lecteurs d'écran par son `aria-modal`. Or
   * c'est précisément là qu'elle sert — un visiteur qui ne lit pas la langue
   * servie reçoit le rempart légal avant toute autre chose.
   *
   * L'entrer dans le dialogue règle les trois d'un coup, sans toucher à la
   * section : dedans, elle est peinte au-dessus de la carte, le piège à focus
   * du gate la ramasse avec le reste — il énumère les focusables du gate — et
   * l'`aria-modal` ne la cache plus.
   *
   * Dans le dialogue, mais pas dans la carte. Elle a d'abord été insérée en
   * tête de `.amb-gate__card`, en flux : la carte, centrée, grandissait et
   * tout son contenu descendait — un décalage de mise en page de 0,174 sur
   * téléphone, mesuré par Lighthouse, la première chose que voyait un visiteur
   * anglophone. Posée juste avant la carte, en enfant direct du gate, elle
   * est mise hors du flux par `.amb-locale-banner--over-gate` (position
   * absolue, en haut de la porte) : rien ne bouge. Le rang dans la tabulation
   * est le même qu'avant — elle précède les boutons de la carte.
   *
   * Le gate se referme en se posant `hidden` à lui-même : c'est cet attribut
   * qu'on observe pour rendre la bannière à sa place, plutôt que d'aller câbler
   * un signal dans la section, qui est le dernier rempart légal et qu'on
   * préfère laisser intacte. Sa place, justement, est retenue avant de bouger :
   * elle est rendue après le lien d'évitement, qui doit rester la première
   * cible du clavier.
   *
   * Sans gate levé, sans carte où entrer, ou sans `MutationObserver` pour voir
   * la sortie, on ne bouge rien : mieux vaut une bannière au bas de la page
   * qu'une bannière enfermée dans un gate refermé.
   *
   * @param {HTMLElement} banner - La bannière, déjà révélée.
   */
  function dockToAgeGate(banner) {
    const gate = document.getElementById('AmbAgeGate');
    if (!gate || gate.hidden || typeof MutationObserver !== 'function') return;
    const card = gate.querySelector('.amb-gate__card');
    if (!(card instanceof HTMLElement)) return;

    const parent = banner.parentNode;
    const next = banner.nextSibling;
    if (!parent) return;

    gate.insertBefore(banner, card);
    banner.classList.add('amb-locale-banner--over-gate');

    const observer = new MutationObserver(() => {
      if (!gate.hidden) return;
      observer.disconnect();
      banner.classList.remove('amb-locale-banner--over-gate');
      // `next` a pu disparaître entre-temps ; `insertBefore` lèverait alors, et
      // la bannière resterait enfermée dans un gate refermé. `null` la remet
      // simplement en fin de conteneur, ce qui vaut toujours mieux.
      parent.insertBefore(banner, next && next.parentNode === parent ? next : null);
    });
    observer.observe(gate, { attributes: true, attributeFilter: ['hidden'] });
  }

  /**
   * Bannière de suggestion de langue.
   *
   * Shopify s'interdit toute redirection automatique de langue à l'intérieur de
   * l'UE : proposer est le seul geste disponible. La suggestion vient de
   * `browsing_context_suggestions.json`, qui croise l'adresse IP et
   * `Accept-Language`, et ne répond que si la boutique publie effectivement la
   * langue en question.
   *
   * Elle ne répond donc rien à un visiteur francophone, à qui la boutique sert
   * l'allemand par défaut. D'où le repli : quand la boutique n'a rien à dire,
   * on regarde `navigator.languages`. Si la langue servie n'y figure pas, le
   * visiteur ne lit pas cette page, et on lui propose la langue principale de
   * la boutique — le bloc marqué `data-amb-locale-fallback`.
   *
   * Le texte est déjà dans la page, rendu par langue disponible et masqué par
   * `snippets/ambinoid-locale-banner.liquid` : il n'y a ici qu'à révéler le bon
   * bloc. Aucune chaîne visible ne vit dans ce fichier.
   */
  function initLocaleBanner() {
    const found = document.querySelector('[data-amb-locale-banner]');
    if (!(found instanceof HTMLElement) || found.dataset.ambReady === '1') return;
    if (typeof window.fetch !== 'function') return;
    const banner = found;
    banner.dataset.ambReady = '1';

    const KEY = 'ambinoid:locale-dismissed';

    try {
      if (window.localStorage.getItem(KEY) === '1') return;
    } catch (error) {
      // Navigation privée : la bannière pourra réapparaître, sans plus.
    }

    banner.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element) || !target.closest('[data-amb-locale-dismiss]')) return;
      banner.hidden = true;
      try {
        window.localStorage.setItem(KEY, '1');
      } catch (error) {
        // Rien à mémoriser : la suggestion repassera à la prochaine visite.
      }
    });

    /**
     * Découvre une offre, et la bannière avec elle.
     *
     * @param {Element|null} offer - Le bloc à montrer, s'il existe.
     * @returns {boolean} Vrai si quelque chose a été révélé.
     */
    function show(offer) {
      if (!(offer instanceof HTMLElement)) return false;
      offer.hidden = false;
      banner.hidden = false;
      dockToAgeGate(banner);
      return true;
    }

    /**
     * Révèle l'offre correspondant à la langue suggérée.
     *
     * @param {string} handle - Code de langue renvoyé par la boutique.
     * @returns {boolean} Vrai si la langue avait bien un bloc dans la page.
     */
    function reveal(handle) {
      // Le code vient du réseau et sert à bâtir un sélecteur : on ne laisse
      // passer que ce qui a la forme d'un code de langue.
      if (!/^[a-z]{2,3}(-[a-zA-Z]{2,4})?$/.test(handle)) return false;
      // Langue publiée par la boutique mais sans texte dans le snippet : `show`
      // ne trouve rien et rend la main au repli.
      return show(banner.querySelector('[data-amb-locale-offer="' + handle + '"]'));
    }

    /**
     * Repli : la boutique n'a rien proposé.
     *
     * Reste à savoir si le visiteur lit seulement la page qu'il a sous les yeux.
     * `navigator.languages` porte ses langues par ordre de préférence ; on les
     * compare sur la sous-étiquette primaire, `de-AT` valant `de`.
     *
     * S'il en a une qui correspond, il est chez lui : se taire. Sinon on lui
     * propose la langue principale de la boutique — celle dans laquelle le
     * contenu est rédigé, et la seule qui reste quand la sienne n'est pas
     * publiée. Elle n'est marquée que si elle n'est pas déjà servie : sur la
     * page anglaise, le repli ne trouve rien et se tait.
     */
    function fallback() {
      const current = banner.dataset.ambLocaleCurrent;
      if (!current) return;
      const spoken = navigator.languages || [navigator.language];
      /** @param {string} tag - Étiquette de langue, `de` ou `de-AT`. */
      const base = (tag) => String(tag).toLowerCase().split('-')[0];
      for (const tag of spoken) {
        if (tag && base(tag) === base(current)) return;
      }
      show(banner.querySelector('[data-amb-locale-fallback]'));
    }

    function ask() {
      const shopify = window.Shopify;
      const rawRoot = (shopify && shopify.routes && shopify.routes.root) || '/';
      // `routes.root` porte le préfixe de langue et se termine par une barre
      // (`/`, `/en/`). On la remet si la boutique venait à l'omettre.
      const root = rawRoot.charAt(rawRoot.length - 1) === '/' ? rawRoot : rawRoot + '/';

      const params = new URLSearchParams();
      params.set('language[enabled]', 'true');
      const current = banner.dataset.ambLocaleCurrent;
      // Sans exclusion, la boutique suggère aussi la langue déjà servie.
      if (current) params.set('language[exclude]', current);

      window
        .fetch(root + 'browsing_context_suggestions.json?' + params.toString(), {
          headers: { Accept: 'application/json' },
        })
        .then((response) => (response.ok ? response.json() : Promise.reject(new Error('http'))))
        .then((payload) => {
          const suggestions = payload && payload.suggestions;
          if (Array.isArray(suggestions)) {
            for (const suggestion of suggestions) {
              const language = suggestion && suggestion.parts && suggestion.parts.language;
              if (language && typeof language.handle === 'string' && reveal(language.handle)) return;
            }
          }
          fallback();
        })
        .catch(() => {
          // Endpoint injoignable ou hors ligne. Le repli, lui, ne dépend pas du
          // réseau : ce que le visiteur lit se demande au navigateur.
          fallback();
        });
    }

    ask();
  }

  /* ---------- vidéo de fond du hero ---------- */

  /**
   * Démarre la vidéo de fond du hero, si la page en porte une.
   *
   * Le réglage est optionnel et l'image reste obligatoire : ce que ce code
   * pilote est une couche posée sur elle, jamais un remplacement. Il n'y a donc
   * pas de chemin où l'échec de cette fonction laisse le hero vide.
   *
   * Trois choses vivent ici et nulle part ailleurs, et aucune ne se relit dans
   * le Liquid ni dans la feuille :
   *
   * - LE MOMENT. La vidéo n'a pas d'`autoplay`, elle a `preload="none"`, et
   *   c'est `play()` qui déclenche le premier octet. Cet appel attend la
   *   fermeture de l'age gate, qui est opaque, couvre la fenêtre et se peint
   *   avant le reste de la boutique : sans cette attente, une première visite
   *   paierait plusieurs mégaoctets de trames derrière une porte fermée, sur un
   *   réseau mobile, pour quelqu'un qui n'a encore rien demandé. Rien dans le
   *   balisage ne dit qu'un `autoplay` coûterait ça.
   * - LE MOUVEMENT RÉDUIT. On ne démarre pas, donc rien ne part sur le réseau
   *   non plus — une règle CSS n'aurait su que cacher une vidéo déjà
   *   téléchargée, et pas l'arrêter. C'est le seul endroit où ce choix est
   *   écrit.
   * - LA RÉVÉLATION. `data-amb-playing` suit l'événement `playing`, donc des
   *   trames réellement arrivées, et pas l'appel à `play()` qui peut être
   *   refusé sans bruit (politique d'autoplay, économiseur de batterie iOS) ou
   *   n'aboutir jamais (source injoignable). Révéler sur l'appel remplacerait
   *   la photo par un rectangle vide dans exactement ces cas-là, qui sont ceux
   *   qu'on ne reproduit pas en regardant la page.
   *
   * Éprouvée par tests/hero-video.test.mjs.
   *
   * @param {HTMLElement} root - `.amb-hero__media` portant `data-amb-hero-video`.
   */
  function initHeroVideo(root) {
    if (root.dataset.ambReady === '1') return;
    root.dataset.ambReady = '1';

    const found = root.querySelector('video');
    if (!(found instanceof HTMLVideoElement)) return;
    const video = found;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    whenClosed(document.getElementById('AmbAgeGate'), () => {
      video.addEventListener(
        'playing',
        () => {
          video.dataset.ambPlaying = '1';
        },
        { once: true }
      );

      // `muted` en propriété et pas seulement en attribut : c'est la propriété
      // que la politique d'autoplay interroge, et l'attribut seul ne suffit pas
      // si quoi que ce soit l'a touchée entre-temps.
      video.muted = true;
      // `preload` repasse à `auto` ici, et pas dans le Liquid : c'est ce qui
      // fait que « rien avant la porte » est tenu par le même geste que
      // « lecture après la porte », plutôt que par deux réglages qui peuvent
      // diverger.
      video.preload = 'auto';
      video.play().catch(() => {
        // Refus de la politique d'autoplay, économiseur de batterie iOS, source
        // injoignable. L'image reste seule : c'est le repli voulu, et le silence
        // est délibéré — rien à dire au visiteur, rien de cassé.
      });
    });
  }

  /* ---------- aura des cartes de fleurs ---------- */

  /**
   * Observateur partagé par toutes les auras de la page : une carte de plus
   * n'en crée pas un de plus, et `init()` rejoué par l'éditeur retrouve le
   * même.
   *
   * @type {IntersectionObserver | null}
   */
  let auraObserver = null;

  /**
   * Fait éclore l'aura d'une carte quand elle entre à l'écran.
   *
   * L'aura est déjà peinte par le Liquid et la feuille ; ce script ne fait que
   * la cacher (`wait`) puis la révéler (`in`). Il ne cache que ce qu'il
   * surveille, et il ne surveille que s'il peut la révéler : sans
   * `IntersectionObserver`, ou sous le mouvement réduit, il ne touche à rien,
   * et l'aura reste là, immobile. Une carte rendue après coup — filtres,
   * « Zuletzt angesehen » — que rien n'a observée est dans le même cas.
   *
   * Le délai échelonne les cartes d'un même lot, de gauche à droite : une
   * rangée qui entre d'un coup éclot en vague plutôt qu'en bloc. Il est
   * plafonné, pour qu'une grille entière à l'arrivée ne fasse pas attendre la
   * dernière.
   *
   * Éprouvée par tests/product-card-aroma.test.mjs.
   *
   * @param {HTMLElement} aura - `.amb-pcard__aura` portant `data-amb-aura`.
   */
  function initAura(aura) {
    if (aura.dataset.ambReady === '1') return;
    aura.dataset.ambReady = '1';

    if (!('IntersectionObserver' in window)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    if (auraObserver === null) {
      auraObserver = new IntersectionObserver(
        (entries, observer) => {
          let rank = 0;
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            observer.unobserve(entry.target);
            const target = entry.target.querySelector('[data-amb-aura]');
            if (!(target instanceof HTMLElement)) continue;
            target.style.setProperty('--amb-aura-delay', `${Math.min(rank, 5) * 90}ms`);
            target.dataset.ambAuraState = 'in';
            rank += 1;
          }
        },
        { threshold: 0.35 }
      );
    }

    // Le cadre, pas l'aura : en attente, l'aura est agrandie à 1,35 et rognée
    // par le cadre, donc sa propre part visible plafonnerait à 55 % — un seuil
    // qui dépendrait de l'échelle de l'animation.
    const frame = aura.parentElement;
    if (frame === null) return;
    aura.dataset.ambAuraState = 'wait';
    auraObserver.observe(frame);
  }

  /* ---------- démarrage ---------- */

  /**
   * Lance une initialisation en isolant son échec.
   *
   * `init()` était une seule suite d'appels, et une exception dans n'importe
   * lequel emportait tous les suivants sans rien dans la page pour le dire. La
   * fiche produit part en premier, l'infolettre en sixième : un accident dans
   * l'une laissait donc le formulaire de l'autre partir par la page, avec le
   * rechargement et les 666 ms de défilement animé que le `fetch` existe pour
   * éviter. Le mini-panier, les filtres et le tiroir sont derrière elle.
   *
   * Une section qui casse ne doit casser qu'elle. La console est le seul endroit
   * où le dire : rien de visible ne doit changer parce qu'une section a échoué,
   * chacune ayant déjà son comportement de repli en CSS ou en Liquid.
   *
   * @param {string} label - Ce qu'on initialisait, pour la console.
   * @param {() => void} apply - L'initialisation à lancer.
   */
  function guard(label, apply) {
    try {
      apply();
    } catch (error) {
      console.error('[ambinoid]', label, error);
    }
  }

  /**
   * Applique une initialisation à chaque racine portant l'attribut.
   *
   * Deux niveaux d'isolement : la recherche des racines, et chaque racine prise
   * à part, une occurrence cassée ne devant pas emporter ses voisines,
   * l'éditeur de thème permettant plusieurs fois la même section dans une page.
   *
   * @param {string} selector - L'attribut qui désigne les racines.
   * @param {(el: HTMLElement) => void} apply - L'initialisation à appliquer.
   */
  function each(selector, apply) {
    guard(selector, () => {
      for (const el of document.querySelectorAll(selector)) {
        if (el instanceof HTMLElement) guard(selector, () => apply(el));
      }
    });
  }

  function init() {
    each('[data-amb-product-root]', initProductForm);
    each('[data-amb-related]', initRelated);
    each('[data-amb-recent]', initRecent);
    each('[data-amb-ticker]', initTicker);
    each('[data-amb-hero-video]', initHeroVideo);
    each('[data-amb-aura]', initAura);
    each('[data-amb-map]', initMap);
    each('[data-amb-map-consent]', initMapConsent);
    each('[data-amb-newsletter-fields]', initNewsletter);
    each('[data-amb-filters]', initFilters);
    each('[data-amb-range]', initRange);
    // Après les curseurs : la feuille retient l'état qu'ils viennent de
    // normaliser, sans quoi elle se croirait modifiée dès l'ouverture.
    each('[data-amb-sheet]', initSheet);
    each('[data-amb-faq]', initFaq);
    each('[data-amb-more]', initMore);
    each('[data-amb-legal]', initLegal);
    guard('initSheetDrag', initSheetDrag);
    guard('initStickyHeader', initStickyHeader);
    guard('initHeaderSearch', initHeaderSearch);
    guard('initSearchSuggest', initSearchSuggest);
    guard('initDrawer', initDrawer);
    guard('initCartDrawer', initCartDrawer);
    guard('initArticle', initArticle);
    // Avant la suggestion de langue : les deux se posent au même coin, et
    // c'est la carte de consentement qui publie la hauteur dont l'autre se
    // décale.
    guard('initCookieBanner', initCookieBanner);
    guard('initLocaleBanner', initLocaleBanner);
  }

  init();

  // Rejoue l'initialisation après un re-rendu de section dans l'éditeur de thème.
  document.addEventListener('shopify:section:load', init);
})();
